import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import * as XLSX from 'xlsx';

// ── System Suggested Days by month (0=Jan … 11=Dec) ──────────
const MP_SUGGESTIONS = {
  0: [{date:'01-01',name:"New Year's Day",cat:'Public Holiday'},{date:'14-01',name:'Makar Sankranti / Pongal',cat:'Festival'},{date:'26-01',name:'Republic Day',cat:'Public Holiday'},{date:'25-01',name:"National Voters' Day",cat:'Awareness Day'}],
  1: [{date:'04-02',name:'World Cancer Day',cat:'Awareness Day'},{date:'14-02',name:"Valentine's Day",cat:'Social Media Day'},{date:'28-02',name:'National Science Day',cat:'Awareness Day'}],
  2: [{date:'08-03',name:"International Women's Day",cat:'Social Media Day'},{date:'22-03',name:'World Water Day',cat:'Awareness Day'},{date:'29-03',name:'Holi',cat:'Festival'}],
  3: [{date:'01-04',name:"April Fool's Day",cat:'Social Media Day'},{date:'07-04',name:'World Health Day',cat:'Awareness Day'},{date:'14-04',name:'Ambedkar Jayanti',cat:'Public Holiday'},{date:'22-04',name:'Earth Day',cat:'Social Media Day'}],
  4: [{date:'01-05',name:'Labour Day',cat:'Public Holiday'},{date:'12-05',name:"Mother's Day",cat:'Social Media Day'},{date:'31-05',name:'World No Tobacco Day',cat:'Awareness Day'}],
  5: [{date:'05-06',name:'World Environment Day',cat:'Awareness Day'},{date:'15-06',name:"Father's Day",cat:'Social Media Day'},{date:'21-06',name:'International Yoga Day',cat:'Social Media Day'},{date:'30-06',name:'Social Media Day',cat:'Social Media Day'}],
  6: [{date:'01-07',name:"Doctor's Day",cat:'Awareness Day'},{date:'17-07',name:'World Emoji Day',cat:'Social Media Day'},{date:'28-07',name:'Guru Purnima',cat:'Festival'}],
  7: [{date:'15-08',name:'Independence Day',cat:'Public Holiday'},{date:'19-08',name:'Onam (tentative)',cat:'Festival'},{date:'26-08',name:'Janmashtami',cat:'Festival'}],
  8: [{date:'05-09',name:"Teacher's Day",cat:'Social Media Day'},{date:'21-09',name:'International Day of Peace',cat:'Awareness Day'},{date:'27-09',name:'World Tourism Day',cat:'Social Media Day'}],
  9: [{date:'02-10',name:'Gandhi Jayanti',cat:'Public Holiday'},{date:'10-10',name:'World Mental Health Day',cat:'Awareness Day'},{date:'12-10',name:'Dussehra',cat:'Festival'},{date:'31-10',name:'Halloween',cat:'Social Media Day'}],
  10:[{date:'01-11',name:'Diwali',cat:'Festival'},{date:'14-11',name:"Children's Day",cat:'Social Media Day'},{date:'25-11',name:'Guru Nanak Jayanti',cat:'Festival'}],
  11:[{date:'01-12',name:'World AIDS Day',cat:'Awareness Day'},{date:'10-12',name:'Human Rights Day',cat:'Awareness Day'},{date:'25-12',name:'Christmas',cat:'Festival'},{date:'31-12',name:"New Year's Eve",cat:'Social Media Day'}],
};

const CAT_COLORS = {
  'Public Holiday':  '#e84545',
  'Festival':        '#e87830',
  'Social Media Day':'#3b7ef0',
  'Brand Event':     '#6c58f0',
  'Sale / Offer Day':'#1db87a',
  'Awareness Day':   '#f7c34f',
  'Other':           '#9050e8',
};
const CATEGORIES = Object.keys(CAT_COLORS);

// ── Add Custom Day (extracted to prevent focus loss on typing) ──
const AddCustomDay = ({ masterDays, setMasterDays, savePersist, selections, allowances }) => {
  const [customDate, setCustomDate] = useState('');
  const [customName, setCustomName] = useState('');
  const [customCat,  setCustomCat]  = useState('Public Holiday');

  const addDay = () => {
    if (!customDate || !customName.trim()) { alert('Date and name required.'); return; }
    if (masterDays.find(d => d.date === customDate && d.name === customName.trim())) { alert('Already in list.'); return; }
    const newDays = [...masterDays, { date: customDate, name: customName.trim(), cat: customCat, notes: '' }];
    setMasterDays(newDays);
    savePersist(newDays, selections, allowances);
    setCustomDate(''); setCustomName('');
  };

  return (
    <div className="card mb-16">
      <div className="card-header"><div className="card-title">\u2795 Add Custom Day</div></div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 2fr 180px auto',gap:'12px',alignItems:'flex-end'}}>
        <div className="form-group" style={{margin:0}}>
          <label className="form-label">Date</label>
          <input type="date" className="form-input" value={customDate} onChange={e=>setCustomDate(e.target.value)} />
        </div>
        <div className="form-group" style={{margin:0}}>
          <label className="form-label">Event / Day Name</label>
          <input className="form-input" placeholder="e.g. Onam Offer Day" value={customName} onChange={e=>setCustomName(e.target.value)} />
        </div>
        <div className="form-group" style={{margin:0}}>
          <label className="form-label">Category</label>
          <select className="form-select" value={customCat} onChange={e=>setCustomCat(e.target.value)}>
            {CATEGORIES.map(c=><option key={c}>{c}</option>)}
          </select>
        </div>
        <button className="btn btn-primary" style={{marginBottom:0}} onClick={addDay}>+ Add</button>
      </div>
    </div>
  );
};

// Safe getter — handles both string and {id, name} object from API
const clientName = (c) => (c && typeof c === 'object') ? (c.name || String(c.id||'')) : (c || '');
const safeStr    = (v) => (v && typeof v === 'object') ? JSON.stringify(v) : String(v||'');

// ── Platform SVG brand logos (matching blueprint exactly) ─────────────────
let _svgNonce = 0;
const PLATFORM_SVG = {
  'Instagram':   `<svg viewBox="0 0 32 32" width="100%" height="100%"><defs><linearGradient id="igG" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#feda77"/><stop offset=".5" stop-color="#f58529"/><stop offset="1" stop-color="#dd2a7b"/></linearGradient></defs><rect x="3" y="3" width="26" height="26" rx="7" fill="url(#igG)"/><circle cx="16" cy="16" r="6.2" fill="none" stroke="#fff" stroke-width="2.2"/><circle cx="22.5" cy="9.5" r="1.6" fill="#fff"/></svg>`,
  'Facebook':    `<svg viewBox="0 0 32 32" width="100%" height="100%"><circle cx="16" cy="16" r="14" fill="#1877f2"/><path d="M19.4 16h-2.5v9h-3.5v-9H11.7v-3h1.7v-2c0-2.4 1.5-3.7 3.6-3.7l2.3 0v3h-1.7c-.6 0-.9.3-.9 1v1.7h2.7l-.4 3z" fill="#fff"/></svg>`,
  'LinkedIn':    `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#0a66c2"/><rect x="7" y="12" width="3.5" height="13" fill="#fff"/><circle cx="8.75" cy="8.75" r="2" fill="#fff"/><path d="M13 12h3.4v1.8c.5-.9 1.7-2 3.6-2 3.4 0 4 2.2 4 5.1V25h-3.6v-6.8c0-1.6-.3-2.8-2-2.8s-2.3 1.3-2.3 2.6V25H13V12z" fill="#fff"/></svg>`,
  'YouTube':     `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect x="2" y="6.5" width="28" height="19" rx="4" fill="#ff0000"/><polygon points="13,11.5 23,16 13,20.5" fill="#fff"/></svg>`,
  'Google Ads':  `<svg viewBox="0 0 32 32" width="100%" height="100%"><path d="M14.5 4l-9 15.5a4 4 0 0 0 1.5 5.5 4 4 0 0 0 5.5-1.5l9-15.5a4 4 0 0 0-1.5-5.5A4 4 0 0 0 14.5 4z" fill="#fbbc04"/><path d="M17.5 4l9 15.5a4 4 0 0 1-1.5 5.5 4 4 0 0 1-5.5-1.5l-9-15.5a4 4 0 0 1 1.5-5.5A4 4 0 0 1 17.5 4z" fill="#4285f4"/><circle cx="9" cy="24" r="4" fill="#34a853"/></svg>`,
  'X / Twitter': `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#000"/><path d="M21.5 7h2.8l-6.1 7 7.2 9.7H20l-4.5-6-5.1 6H7.5l6.5-7.5L6.7 7h5.6l4 5.4L21.5 7zm-1 14.9h1.5L11.5 8.5H9.9l10.6 13.4z" fill="#fff"/></svg>`,
  'Pinterest':   `<svg viewBox="0 0 32 32" width="100%" height="100%"><circle cx="16" cy="16" r="14" fill="#e60023"/><path d="M16.4 7c-5 0-7.5 3.3-7.5 6.2 0 1.7.7 3.3 2.3 3.9.3.1.5 0 .6-.3l.2-.9c.1-.3 0-.4-.2-.6-.5-.6-.8-1.3-.8-2.4 0-3.1 2.4-5.9 6.2-5.9 3.4 0 5.3 2 5.3 4.7 0 3.6-1.6 6.6-4 6.6-1.3 0-2.3-1.1-2-2.4.4-1.6 1.2-3.4 1.2-4.5 0-1-.6-1.9-1.7-1.9-1.4 0-2.5 1.4-2.5 3.3 0 1.2.4 2 .4 2l-1.7 7.1c-.5 2.1-.1 4.7 0 5 0 .1.2.2.3.1.1-.1 1.7-2.1 2.2-4.2.2-.6.9-3.6.9-3.6.5.8 1.7 1.6 3.1 1.6 4.1 0 6.9-3.7 6.9-8.7 0-3.8-3.2-7.3-8-7.3z" fill="#fff"/></svg>`,
  'WhatsApp':    `<svg viewBox="0 0 32 32" width="100%" height="100%"><circle cx="16" cy="16" r="14" fill="#25d366"/><path d="M22.4 18.7c-.4-.2-2.1-1-2.4-1.1-.3-.1-.6-.2-.8.2-.2.3-.9 1.1-1.1 1.3-.2.2-.4.3-.7.1-2.1-1-3.4-1.9-4.8-4.2-.4-.6.4-.6 1-2 .1-.2 0-.4-.1-.6l-1.1-2.6c-.3-.7-.6-.6-.8-.6-.2 0-.4 0-.6 0-.2 0-.6.1-.9.4-.3.3-1.2 1.2-1.2 2.9 0 1.7 1.2 3.4 1.4 3.6.2.2 2.4 3.6 5.8 5.1 2.1.9 3 1 4 .8.6-.1 1.9-.8 2.2-1.6.3-.8.3-1.4.2-1.6-.1-.2-.3-.3-.7-.5z" fill="#fff"/></svg>`,
  'Telegram':    `<svg viewBox="0 0 32 32" width="100%" height="100%"><circle cx="16" cy="16" r="14" fill="#229ed9"/><path d="M9 16.2l13.2-5.1c.6-.2 1.2.1 1 1L20.9 22c-.2.7-.6.9-1.2.6l-3.4-2.5-1.6 1.6c-.2.2-.4.3-.7.3l.3-3.6 6.7-6c.3-.3-.1-.4-.4-.2l-8.3 5.2-3.6-1.1c-.8-.2-.8-.7.3-1z" fill="#fff"/></svg>`,
  'Snapchat':    `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#fffc00"/><path d="M16 6c3.5 0 6 2.4 6 5.9v3.5c.2.1.6.3 1.2.3.4 0 .9-.2 1.2-.4l.4 1.1c-.3.2-1 .6-2 .6-.4 0-.8-.1-1.1-.2 0 .4-.1.8-.2 1.2 1.4.3 2.7.9 3 1.1l-.5 1.1c-.3-.2-1.4-.7-2.6-1-.7 1.4-2 2.6-4.4 3-.3 0-.5.7-1.5.7s-1.2-.6-1.5-.7c-2.4-.4-3.7-1.6-4.4-3-1.2.3-2.3.8-2.6 1l-.5-1.1c.3-.2 1.6-.8 3-1.1-.1-.4-.2-.8-.2-1.2-.3.1-.7.2-1.1.2-1 0-1.7-.4-2-.6l.4-1.1c.3.2.8.4 1.2.4.6 0 1-.2 1.2-.3v-3.5C10 8.4 12.5 6 16 6z" fill="#000"/></svg>`,
  'TikTok':      `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#010101"/><path d="M22.2 12.7c-1.6-.3-2.9-1.3-3.6-2.7h-3v11.5c0 1.5-1.2 2.7-2.7 2.7s-2.7-1.2-2.7-2.7 1.2-2.7 2.7-2.7c.3 0 .5 0 .8.1V16c-.3 0-.5-.1-.8-.1-3 0-5.5 2.5-5.5 5.5s2.5 5.5 5.5 5.5 5.5-2.5 5.5-5.5v-6c1.2.7 2.6 1.1 4 1.1v-3.6c-.1-.2-.1-.2-.2-.2z" fill="#fff"/></svg>`,
  'Email / CRM': `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect x="3" y="7" width="26" height="18" rx="3" fill="#e87830"/><path d="M3 9.5L16 17 29 9.5" stroke="#fff" stroke-width="2" fill="none" stroke-linejoin="round"/></svg>`,
  'Amazon Ads':  `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#232f3e"/><path d="M9 13l2-.5v8.5c0 .8.5 1.5 1.5 1.5h7c1 0 1.5-.7 1.5-1.5V12.5l2 .5-.5-2-13 .5L9 13z" fill="#ff9900"/></svg>`,
  'Flipkart Ads':`<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#2874f0"/><path d="M9 9h14v3h-7v3h6v3h-6v6h-3V12H9V9z" fill="#fff"/></svg>`,
  'Spotify':     `<svg viewBox="0 0 32 32" width="100%" height="100%"><circle cx="16" cy="16" r="14" fill="#1db954"/><path d="M22.6 21.5c-.3.4-.8.6-1.2.3-3.4-2.1-7.6-2.5-12.6-1.4-.5.1-1-.2-1.1-.7-.1-.5.2-1 .7-1.1 5.4-1.2 10.1-.7 13.8 1.6.5.3.6.8.4 1.3zm1.7-3.4c-.4.5-1 .7-1.5.4-3.9-2.4-9.8-3.1-14.4-1.7-.6.2-1.3-.2-1.5-.8-.2-.6.2-1.3.8-1.5 5.3-1.6 11.8-.8 16.3 2 .5.3.7 1 .3 1.6zm.1-3.6c-4.6-2.7-12.3-3-16.7-1.7-.8.2-1.6-.2-1.8-1-.2-.8.2-1.6 1-1.8 5-1.5 13.5-1.2 18.8 1.9.7.4 1 1.3.5 2-.4.7-1.3.9-1.8.6z" fill="#fff"/></svg>`,
  'Meta Ads':    `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#1877f2"/><path d="M6 16c0-4.7 2.7-9 7.5-9 2.8 0 4.7 1.5 6.4 3.7 1.6-2.1 3.5-3.7 6.1-3.7 4 0 6 3.4 6 7.4 0 5.6-3.4 11.6-7.7 11.6-2.4 0-3.9-1.4-5.4-3.7-1.8-2.4-2.8-4.2-3.9-4.2-1.3 0-2.2 1.5-2.8 3.2-.4 1-.6 2-.6 2.8 0 1.4.6 2.3 1.7 2.3 1.4 0 2.4-1.2 3.5-2.7l1.7 2.4C16.4 26.4 14.7 28 12 28c-3.5 0-6-2.8-6-12z" fill="#fff"/></svg>`,
  'Quora Ads':   `<svg viewBox="0 0 32 32" width="100%" height="100%"><circle cx="16" cy="16" r="14" fill="#a82400"/><path d="M16 7C11 7 7 11 7 16s4 9 9 9c1.7 0 3.2-.5 4.6-1.3l2 2.3 2.2-2-2-2.2c1.4-1.6 2.2-3.6 2.2-5.8 0-5-4-9-9-9zm0 15c-3.3 0-6-2.7-6-6s2.7-6 6-6 6 2.7 6 6c0 1.2-.4 2.4-1 3.3l-2.4-2.7-2.2 2 2.4 2.7c-.8.5-1.7.7-2.8.7z" fill="#fff"/></svg>`,
  'Reddit':      `<svg viewBox="0 0 32 32" width="100%" height="100%"><circle cx="16" cy="16" r="14" fill="#ff4500"/><circle cx="16" cy="18" r="8" fill="#fff"/><circle cx="12.5" cy="17.5" r="1.4" fill="#ff4500"/><circle cx="19.5" cy="17.5" r="1.4" fill="#ff4500"/><path d="M11.5 21c1 1 2.7 1.7 4.5 1.7s3.5-.7 4.5-1.7" stroke="#ff4500" stroke-width="1.4" fill="none" stroke-linecap="round"/><circle cx="23.5" cy="13.5" r="2.2" fill="#fff"/><circle cx="8.5" cy="13.5" r="2.2" fill="#fff"/></svg>`,
  'Discord':     `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#5865f2"/><path d="M22.5 10.5c-1.4-.6-2.9-1.1-4.4-1.4l-.3.5c-1.3-.2-2.7-.2-4 0l-.3-.5c-1.5.3-3 .7-4.4 1.4-2.7 4-3.5 7.9-3.1 11.8 1.7 1.3 3.4 2 5.1 2.5l.4-.6c-.6-.2-1.3-.5-1.9-.9l.5-.3c2.4 1.1 5 1.6 7.6 1.6 2.6 0 5.2-.5 7.6-1.6l.5.3c-.6.4-1.2.7-1.9.9l.4.6c1.7-.5 3.4-1.2 5.1-2.5.4-4.5-.7-8.4-3.1-11.8zm-9.3 9.6c-1 0-1.9-.9-1.9-2.1 0-1.2.9-2.1 1.9-2.1 1 0 1.9.9 1.9 2.1 0 1.2-.9 2.1-1.9 2.1zm5.6 0c-1 0-1.9-.9-1.9-2.1 0-1.2.9-2.1 1.9-2.1 1 0 1.9.9 1.9 2.1 0 1.2-.9 2.1-1.9 2.1z" fill="#fff"/></svg>`,
  'Threads':     `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#000"/><path d="M21.5 14.5c-.1-2.7-1.7-4.5-4.4-4.7-2.6-.2-4.4 1-5.2 2.7l1.6.8c.5-1.1 1.7-1.7 3.5-1.6 1.6.1 2.5 1.1 2.6 2.5-.7-.2-1.4-.3-2.2-.3-2.6 0-4.6 1.5-4.4 3.7.1 1.8 1.7 3 3.7 3 1.7 0 3-1 3.6-2.3.5.8 1 1.5 1.5 2.1l1.4-1.2c-.6-.6-1.4-2-1.7-4.7zm-4.6 4c-.9 0-1.9-.5-1.9-1.5 0-1 1-1.7 2.6-1.7.7 0 1.4.1 2 .3-.1 1.6-1.1 2.9-2.7 2.9z" fill="#fff"/></svg>`,
};
const getPlatformSvg = (name) => {
  const svg = PLATFORM_SVG[name];
  if (!svg) return null;
  const nonce = 'p' + (++_svgNonce);
  return svg
    .replace(/id="([a-zA-Z][\w-]*)"/g, (_, id) => `id="${id}_${nonce}"`)
    .replace(/url\(#([a-zA-Z][\w-]*)\)/g, (_, id) => `url(#${id}_${nonce})`);
};
const PLATFORM_COLORS = {
  'Instagram':'#e8323c','Facebook':'#1877f2','LinkedIn':'#0a66c2','YouTube':'#e84545',
  'X / Twitter':'#1da1f2','WhatsApp':'#25d366','Google Ads':'#4285f4','Meta Ads':'#1877f2',
  'Pinterest':'#e60023','TikTok':'#010101','Snapchat':'#fffc00','Telegram':'#229ed9',
  'Email / CRM':'#e87830','Amazon Ads':'#ff9900','Flipkart Ads':'#2874f0','Spotify':'#1db954',
  'Quora Ads':'#a82400','Reddit':'#ff4500','Discord':'#5865f2','Threads':'#000000',
};


const MONTHS_LONG = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const WEEK_DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function CatPill({ cat }) {
  const color = CAT_COLORS[cat] || '#9050e8';
  return (
    <span style={{
      display:'inline-block', padding:'2px 10px', borderRadius:'20px',
      fontSize:'0.7rem', fontWeight:700,
      background:`${color}18`, color, border:`1px solid ${color}40`
    }}>{cat}</span>
  );
}

// ── Stable calendar sub-components (defined OUTSIDE Planning to prevent remounting) ──
function MiniMonth({ m, y, localWOs, masterDays, lcCl, clientColors, todayStr, setModal, setCalMonth, setLcAll, setEmAll }) {
  const prefix   = `${y}-${String(m+1).padStart(2,'0')}`;
  const firstDay = new Date(y,m,1).getDay();
  const dim      = new Date(y,m+1,0).getDate();
  const total    = Math.ceil((firstDay+dim)/7)*7;
  const mWOs  = localWOs.filter(wo=>{ const d=wo.event_date||wo.date||''; return d.startsWith(prefix)&&(!lcCl||clientName(wo.client)===lcCl); });
  const mSpec = masterDays.filter(d=>d.date.startsWith(prefix));
  const dayWOs={}, daySpec={};
  mWOs.forEach(wo=>{ const d=(wo.event_date||wo.date||'').split('T')[0]; (dayWOs[d]=dayWOs[d]||[]).push(wo); });
  mSpec.forEach(s=>{ (daySpec[s.date]=daySpec[s.date]||[]).push(s); });
  const isCur = prefix===todayStr.slice(0,7);
  return (
    <div style={{background:'var(--bg2)',border:`1px solid ${isCur?'rgba(59,126,240,0.4)':'var(--border)'}`,borderRadius:'8px',overflow:'hidden',boxShadow:isCur?'0 0 0 2px rgba(59,126,240,0.15)':undefined}}>
      <div onClick={()=>{setCalMonth(m,y);setLcAll(false);setEmAll(false);}} title={`Open ${MONTHS_LONG[m]} ${y}`}
        style={{padding:'8px 12px',background:'var(--bg3)',borderBottom:'1px solid var(--border)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between'}}
        onMouseOver={e=>e.currentTarget.style.background='var(--bg2)'} onMouseOut={e=>e.currentTarget.style.background='var(--bg3)'}>
        <span style={{fontWeight:700,fontSize:'0.84rem',color:isCur?'var(--accent)':'var(--text)'}}>{MONTHS_LONG[m]} {y}</span>
        <span style={{fontSize:'0.62rem',color:'var(--text3)'}}>{mWOs.length} event{mWOs.length!==1?'s':''}</span>
      </div>
      <div style={{padding:'6px 8px 8px'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:'2px',fontSize:'0.52rem',color:'var(--text3)',fontWeight:700,textAlign:'center',marginBottom:'3px'}}>
          {['S','M','T','W','T','F','S'].map((d,i)=><div key={i}>{d}</div>)}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:'2px'}}>
          {Array.from({length:total},(_,cell)=>{
            const dn=cell-firstDay+1, inM=dn>=1&&dn<=dim;
            const ds=inM?`${prefix}-${String(dn).padStart(2,'0')}`:null;
            const isTd=ds===todayStr, wos=ds?dayWOs[ds]||[]:[], specs=ds?daySpec[ds]||[]:[];
            return (
              <div key={cell}
                onClick={e=>{ e.stopPropagation(); if(inM&&ds) setModal({mode:'add',prefillDate:ds}); }}
                style={{minHeight:'40px',padding:'2px 3px',borderRadius:'3px',opacity:!inM?0.25:1,cursor:inM?'pointer':undefined,background:isTd?'rgba(59,126,240,0.12)':undefined,border:isTd?'1px solid var(--accent)':'1px solid transparent'}}>
                <div style={{fontSize:'0.6rem',fontWeight:isTd?800:500,color:isTd?'var(--accent)':'var(--text2)',lineHeight:1}}>{inM?dn:''}</div>
                {specs.slice(0,1).map((s,si)=><div key={si} style={{height:'3px',background:CAT_COLORS[s.cat]||'#9050e8',marginTop:'2px',borderRadius:'1px'}} title={s.name} />)}
                {wos.slice(0,3).map((wo,wi)=>{ const col=clientColors[clientName(wo.client)]||'var(--accent)';
                  return <div key={wi}
                    onClick={e=>{e.stopPropagation();setModal({mode:'edit',entry:wo});}}
                    style={{height:'4px',background:col,marginTop:'2px',borderRadius:'1px',cursor:'pointer',opacity:(wo.cached_status||wo.status)==='On Hold'?0.45:1}}
                    title={`${clientName(wo.client)}: ${wo.title||wo.day_name||''}`} />;
                })}
                {wos.length>3 && <div style={{fontSize:'0.5rem',color:'var(--text3)',textAlign:'center',fontWeight:700}}>+{wos.length-3}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FullMonth({ m, y, localWOs, masterDays, lcCl, clientColors, todayStr, setModal }) {
  const prefix=`${y}-${String(m+1).padStart(2,'0')}`, firstDay=new Date(y,m,1).getDay(), dim=new Date(y,m+1,0).getDate(), total=Math.ceil((firstDay+dim)/7)*7;
  const mWOs=localWOs.filter(wo=>{ const d=wo.event_date||wo.date||''; return d.startsWith(prefix)&&(!lcCl||clientName(wo.client)===lcCl); });
  const mSpec=masterDays.filter(d=>d.date.startsWith(prefix));
  const dayWOs={}, daySpec={};
  mWOs.forEach(wo=>{ const d=(wo.event_date||wo.date||'').split('T')[0]; (dayWOs[d]=dayWOs[d]||[]).push(wo); });
  mSpec.forEach(s=>{ (daySpec[s.date]=daySpec[s.date]||[]).push(s); });
  return (
    <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:'6px',padding:'12px'}}>
      {['SUN','MON','TUE','WED','THU','FRI','SAT'].map(d=><div key={d} style={{padding:'6px 10px',textAlign:'center',fontSize:'0.68rem',fontWeight:700,color:'var(--text3)',letterSpacing:'0.5px'}}>{d}</div>)}
      {Array.from({length:total},(_,cell)=>{
        const dn=cell-firstDay+1, inM=dn>=1&&dn<=dim;
        const ds=inM?`${prefix}-${String(dn).padStart(2,'0')}`:null;
        const isTd=ds===todayStr, dow=cell%7, isWE=dow===0||dow===6;
        const wos=ds?dayWOs[ds]||[]:[], specs=ds?daySpec[ds]||[]:[];
        return (
          <div key={cell}
            onClick={e=>{ e.stopPropagation(); if(inM&&ds) setModal({mode:'add',prefillDate:ds}); }}
            style={{background:!inM?'var(--bg3)':isTd?'rgba(59,126,240,0.04)':'var(--bg2)',minHeight:'90px',padding:'6px 5px',border:'1px solid var(--border)',borderTop:isTd?'3px solid var(--accent)':'1px solid var(--border)',borderRadius:'8px',opacity:!inM?0.35:1,cursor:inM?'pointer':undefined}}>
            <div style={{fontSize:'0.72rem',fontWeight:isTd?800:600,color:isTd?'var(--accent)':isWE?'#e84545':'var(--text3)',marginBottom:'4px',padding:'0 2px'}}>{inM?dn:''}</div>
            {specs.map((s,si)=>{ const col=CAT_COLORS[s.cat]||'#9050e8';
              return <div key={si} title={s.name} style={{background:`${col}22`,color:col,borderLeft:`3px solid ${col}`,fontSize:'0.62rem',fontWeight:700,padding:'2px 5px',borderRadius:'3px',marginBottom:'2px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>★ {s.name}</div>;
            })}
            {wos.map((wo,wi)=>{ const col=clientColors[clientName(wo.client)]||'var(--accent)'; const cn2=clientName(wo.client); const dn2=wo.title||wo.day_name||''; const isHold=(wo.cached_status||wo.status)==='On Hold';
              return <div key={wi}
                onClick={e=>{e.stopPropagation();setModal({mode:'edit',entry:wo});}}
                title={`${cn2}: ${dn2}${isHold?' [ON HOLD]':''}`}
                style={{background:col,color:'#fff',opacity:isHold?0.55:1,fontSize:'0.62rem',fontWeight:600,padding:'2px 6px',borderRadius:'3px',marginBottom:'2px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',cursor:'pointer'}}>
                {isHold?'⏸ ':''}{cn2.length>12?cn2.slice(0,12)+'…':cn2}: {dn2.length>10?dn2.slice(0,10)+'…':dn2}
              </div>;
            })}
          </div>
        );
      })}
    </div>
  );
}

// ── BulkFormatsModal — outside Planning to avoid re-render issues ──
function BulkFormatsModal({ outputFormats, selected, onToggle, onClear, onClose, onApply, selCount }) {
  const isSelected = (fname) => (selected||[]).includes(fname);

  const favNames = (() => {
    try { return JSON.parse(localStorage.getItem('workops_fmtFavs') || '[]'); }
    catch(e) { return []; }
  })();

  const allFmts = (outputFormats||[]).filter(f => f.enabled !== false && f.is_active !== false);
  const favFmts = allFmts.filter(f => favNames.includes(f.platform ? `${f.platform} ${f.name||f.label||''}` : (f.name||f.label||'')));

  const PCOLORS = {
    'Instagram':'#e1306c','Facebook':'#1877f2','LinkedIn':'#0077b5','YouTube':'#ff0000',
    'WhatsApp':'#25d366','Pinterest':'#e60023','Google Ads':'#4285f4','X / Twitter':'#000000',
    'Telegram':'#2ca5e0','Snapchat':'#fffc00','TikTok':'#000000','Email / CRM':'#ea4335',
    'Amazon Ads':'#ff9900','Flipkart Ads':'#2874f0','Spotify':'#1db954','Meta Ads':'#0668e1',
    'Threads':'#000000','Discord':'#5865f2','Reddit':'#ff4500','Quora Ads':'#a82400',
  };

  const FormatRow = ({ f, keyPrefix }) => {
    const fname = f.platform ? `${f.platform} ${f.name || f.label || ''}` : (f.name || f.label || '');
    const checked = isSelected(fname);
    const isFav = favNames.includes(fname);
    const pcolor = PCOLORS[f.platform] || '#6b7280';
    const dims = f.width && f.height ? `${f.width}×${f.height} px` : (f.dimensions || '');
    return (
      <label style={{display:'flex',alignItems:'center',gap:'10px',padding:'7px 20px',cursor:'pointer',
        background:checked?`${pcolor}0d`:'transparent',
        borderLeft:checked?`3px solid ${pcolor}`:'3px solid transparent',
        transition:'background 0.1s'}}>
        <input type="checkbox" checked={checked} onChange={()=>onToggle(fname)}
          style={{accentColor:pcolor,width:'14px',height:'14px',flexShrink:0}} />
        <span style={{fontSize:'0.72rem',fontWeight:700,color:pcolor,flexShrink:0,minWidth:'65px'}}>{f.platform||''}</span>
        <span style={{fontSize:'0.82rem',fontWeight:checked?700:400,color:checked?pcolor:'var(--text)',flex:1}}>{fname}</span>
        {dims&&<span style={{fontSize:'0.65rem',color:'var(--text3)',whiteSpace:'nowrap'}}>{dims}</span>}
        <span style={{color:isFav?'#f7c34f':'var(--text3)',fontSize:'0.82rem',flexShrink:0,opacity:isFav?1:0.25}}>★</span>
      </label>
    );
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:9998,display:'flex',alignItems:'center',justifyContent:'center'}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',boxShadow:'0 20px 60px rgba(0,0,0,.3)',width:'560px',maxWidth:'96vw',maxHeight:'88vh',display:'flex',flexDirection:'column'}}>

        {/* Header */}
        <div style={{padding:'14px 20px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
            <span style={{fontSize:'1.1rem'}}>📐</span>
            <span className="fw-700" style={{fontSize:'0.95rem'}}>Assign Output Formats</span>
            <span className="badge badge-blue" style={{fontSize:'0.72rem'}}>{selCount} selected</span>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:'1.1rem',cursor:'pointer',color:'var(--text3)'}}>✕</button>
        </div>

        {/* Favorites */}
        {favFmts.length > 0 && (
          <div style={{padding:'8px 0 4px',borderBottom:'1px solid var(--border)',flexShrink:0}}>
            <div style={{padding:'0 20px 4px',fontSize:'0.65rem',fontWeight:700,color:'#f7c34f',textTransform:'uppercase',letterSpacing:'0.5px'}}>⭐ Favorites (click ★ to remove)</div>
            {favFmts.map(f => <FormatRow key={'fav-'+f.id} f={f} keyPrefix="fav" />)}
          </div>
        )}

        {/* Status bar */}
        <div style={{padding:'6px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:'10px',flexShrink:0,background:'var(--bg3)'}}>
          <span style={{fontSize:'0.75rem',color:'var(--text3)'}}>
            <strong style={{color:'var(--accent)'}}>{selected.length}</strong> format{selected.length!==1?'s':''} selected
          </span>
          {selected.length > 0 && (
            <button onClick={onClear} style={{background:'none',border:'none',fontSize:'0.72rem',color:'var(--text3)',cursor:'pointer',textDecoration:'underline'}}>Clear all</button>
          )}
        </div>

        {/* All formats */}
        <div style={{overflowY:'auto',flex:1,padding:'8px 0'}}>
          {allFmts.map(f => <FormatRow key={f.id} f={f} keyPrefix="all" />)}
        </div>

        {/* Footer */}
        <div style={{padding:'12px 20px',borderTop:'1px solid var(--border)',display:'flex',gap:'8px',justifyContent:'flex-end',flexShrink:0}}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={onApply}
            disabled={!selected.length} style={{opacity:selected.length?1:0.5}}>
            ✓ Apply to {selCount} {selCount===1?'entry':'entries'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Planning() {
  const navigate = useNavigate();
  const location = useLocation();
  const now = new Date();

  // ── Global state ─────────────────────────────────────────────
  const initStep = parseInt(new URLSearchParams(location.search).get('step')) || 1;
  const [step,        setStep]        = useState(initStep);
  const [planMonth,   setPlanMonth]   = useState(now.getMonth());
  const [planYear,    setPlanYear]    = useState(now.getFullYear());
  const [masterDays,  setMasterDays]  = useState([]);   // { date, name, cat, notes }
  const [suggestions, setSuggestions] = useState([]);   // from MP_SUGGESTIONS
  const [sugChecked,  setSugChecked]  = useState({});   // idx → bool
  const [sugNotes,    setSugNotes]    = useState({});   // idx → string
  const [clients,     setClients]     = useState([]);
  const [allowances,  setAllowances]  = useState({});   // clientId → number
  const [selections,  setSelections]  = useState({});   // clientId → [{ date, name, cat }]
  const [selClient,   setSelClient]   = useState(null);
  const [sendVia,     setSendVia]     = useState('WhatsApp');
  const [selectedCl,  setSelectedCl]  = useState([]);   // clients picked for sending
  const [daySelReqs,  setDaySelReqs]  = useState([]);   // backend day-selection requests
  const [workOrders,  setWorkOrders]  = useState([]);
  const [weeklyOff,   setWeeklyOff]   = useState([0, 6]); // fallback: Sun+Sat; overridden from tenant
  const [leadDays,    setLeadDays]    = useState(3);      // working days before event; from tenant settings
  const [holidays,    setHolidays]    = useState([]);     // from /api/holidays
  const [platforms,   setPlatforms]   = useState([]);
  const [campaignTypes,setCampaignTypes] = useState([]);
  const [outputFormats,setOutputFormats] = useState([]);
  const [roles,       setRoles]       = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [calMonth,    setCalMonth]    = useState(now.getMonth());
  const [calYear,     setCalYear]     = useState(now.getFullYear());
  const [calAllMonths,setCalAllMonths]= useState(false);
  const [calClient,   setCalClient]   = useState('');
  const [entryMonth,  setEntryMonth]  = useState(now.getMonth());
  const [entryYear,   setEntryYear]   = useState(now.getFullYear());
  const [entryAll,    setEntryAll]    = useState(false);
  const [csvFile,     setCsvFile]     = useState(null);

  // Custom day form

  // ── Load data ────────────────────────────────────────────────
  useEffect(() => {
    api.get('/clients?active_only=false').then(r => {
      const cl = r.data?.data || r.data || [];
      setClients(cl);
      const a = {}; cl.forEach(c => { a[c.id] = allowances[c.id] || 3; });
      setAllowances(a);
      if (cl.length) setSelClient(cl[0].id);
      setSelectedCl(cl.map(c => c.id));
    }).catch(()=>{});
    api.get('/work-orders').then(r => setWorkOrders(r.data?.data || r.data || [])).catch(()=>{});
    api.get('/platforms').then(r => setPlatforms(r.data?.data || r.data || [])).catch(()=>{});
    api.get('/auth/me').then(r => {
      const t = r.data?.tenant || r.data?.data?.tenant;
      if (t) {
        if (Array.isArray(t.weekly_off) && t.weekly_off.length) setWeeklyOff(t.weekly_off);
        if (t.scheduling_lead_days != null) setLeadDays(Number(t.scheduling_lead_days));
      }
    }).catch(()=>{});
    api.get('/holidays').then(r => setHolidays(r.data?.data || r.data || [])).catch(()=>{});
    api.get('/campaign-types').then(r => setCampaignTypes(r.data?.data || r.data || [])).catch(()=>{});
    api.get('/output-formats').then(r => setOutputFormats(r.data?.data || r.data || [])).catch(()=>{});
    api.get('/roles').then(r => setRoles(r.data?.data || r.data || [])).catch(()=>{});
    api.get('/team-members').then(r => setTeamMembers(r.data?.data || r.data || [])).catch(()=>{});
    // Restore from localStorage
    const saved = localStorage.getItem('leadex_planning');
    if (saved) { try { const d = JSON.parse(saved); setMasterDays(d.masterDays||[]); setSelections(d.selections||{}); setAllowances(d.allowances||{}); } catch(e){} }
  }, []);

  // ── Fetch day-selection requests from backend ────────────────
  const fetchDaySelReqs = (month) => {
    api.get(`/day-selections?month=${month}`).then(r => {
      const reqs = r.data?.data || [];
      setDaySelReqs(reqs);
      // Merge submitted selections into local state
      const newSel = { ...selections };
      reqs.forEach(req => {
        if ((req.status === "submitted" || req.status === "agency_filled") && req.selections) {
          newSel[req.client_id] = req.selections;
        }
      });
      setSelections(newSel);
    }).catch(() => {});
  };

  const savePersist = (md, sel, alw) => {
    localStorage.setItem('leadex_planning', JSON.stringify({ masterDays: md, selections: sel, allowances: alw }));
  };

  // ── Step helpers ─────────────────────────────────────────────
  const goStep = (s) => setStep(s);
  const planPrefix = () => `${planYear}-${String(planMonth+1).padStart(2,'0')}`;

  // ── Month-filtered master days ───────────────────────────────
  // Fetch day-selection requests whenever month changes
  useEffect(() => { fetchDaySelReqs(planPrefix()); }, [planMonth, planYear]); // eslint-disable-line
  const monthMasterDays = masterDays.filter(d => (d.date||'').startsWith(planPrefix()));

  // ── Load suggestions ─────────────────────────────────────────
  const loadSuggestions = () => {
    const sugs = (MP_SUGGESTIONS[planMonth] || []).map(s => {
      const [dd, mm] = s.date.split('-');
      return { ...s, date: `${planYear}-${mm}-${dd}`, notes: '' };
    });
    setSuggestions(sugs);
    setSugChecked({});
    setSugNotes({});
  };

  const addSuggestedDays = () => {
    const toAdd = suggestions.filter((_, i) => sugChecked[i]);
    if (!toAdd.length) { alert('Select at least one suggestion.'); return; }
    let added = 0;
    const newDays = [...masterDays];
    toAdd.forEach((s, i) => {
      const note = sugNotes[suggestions.indexOf(s)] || '';
      if (!newDays.find(d => d.date === s.date && d.name === s.name)) {
        newDays.push({ date: s.date, name: s.name, cat: s.cat, notes: note });
        added++;
      }
    });
    setMasterDays(newDays);
    savePersist(newDays, selections, allowances);
    setSugChecked({});
  };

  // ── Add custom day ───────────────────────────────────────────


  const removeMasterDay = (idx) => {
    const newDays = masterDays.filter((_, i) => i !== idx);
    setMasterDays(newDays);
    savePersist(newDays, selections, allowances);
  };

  const resetMonthPlan = () => {
    if (!window.confirm("Reset this month's plan? Master days and client selections for this month will be cleared. Other months kept.")) return;
    const prefix = planPrefix();
    const newDays = masterDays.filter(d => !(d.date||'').startsWith(prefix));
    const newSel  = { ...selections };
    clients.forEach(c => { if (newSel[c.id]) newSel[c.id] = newSel[c.id].filter(d => !(d.date||'').startsWith(prefix)); });
    setMasterDays(newDays);
    setSelections(newSel);
    savePersist(newDays, newSel, allowances);
  };

  // ── Excel template download ──────────────────────────────────
  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const header = ['Date (DD-MM-YYYY)', 'Day Name', 'Category (see Categories sheet)', 'Notes'];
    const sample = [
      ['02-10-2026', 'Gandhi Jayanti', 'Public Holiday', ''],
      ['12-10-2026', 'Dussehra', 'Festival', ''],
      ['31-10-2026', 'Halloween', 'Social Media Day', 'Optional note'],
      ['', '', '', ''],
    ];
    const ws = XLSX.utils.aoa_to_sheet([header, ...sample]);
    ws['!cols'] = [{ wch: 20 }, { wch: 30 }, { wch: 35 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Special Days');

    // Categories reference sheet
    const catData = [
      ['Valid Categories'],
      ['Copy the exact text below into the Category column'],
      [''],
      ...CATEGORIES.map(c => [c]),
    ];
    const wsCat = XLSX.utils.aoa_to_sheet(catData);
    wsCat['!cols'] = [{ wch: 25 }];
    XLSX.utils.book_append_sheet(wb, wsCat, 'Categories');

    // Instructions sheet
    const instrData = [
      ['Special Days — Bulk Upload Instructions'],
      [''],
      ['Column', 'Format', 'Required', 'Example'],
      ['Date', 'DD-MM-YYYY', 'Yes', '02-10-2026'],
      ['Day Name', 'Text', 'Yes', 'Gandhi Jayanti'],
      ['Category', 'Must match a value from Categories sheet', 'Yes', 'Public Holiday'],
      ['Notes', 'Text (optional)', 'No', 'Optional note'],
      [''],
      ['Important:'],
      ['  • Date must be in DD-MM-YYYY format (e.g. 02-10-2026)'],
      ['  • Category must exactly match one of the values in the Categories sheet'],
      ['  • Unrecognized categories will be set to "Other"'],
      ['  • Duplicate entries (same date + name) are automatically skipped'],
    ];
    const wsInstr = XLSX.utils.aoa_to_sheet(instrData);
    wsInstr['!cols'] = [{ wch: 10 }, { wch: 40 }, { wch: 12 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, wsInstr, 'Instructions');

    XLSX.writeFile(wb, 'special_days_template.xlsx');
  };

  const uploadFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const isExcel = file.name.match(/\.xlsx?$/i);

    const processRows = (rows) => {
      const newDays = [...masterDays];
      let added = 0;
      rows.forEach(row => {
        let date = (row[0] || '').toString().trim();
        const name = (row[1] || '').toString().trim();
        const cat  = (row[2] || 'Other').toString().trim();
        const notes = (row[3] || '').toString().trim();
        // Handle Excel serial date numbers
        if (/^\d{5}$/.test(date)) {
          const d = new Date((parseInt(date) - 25569) * 86400000);
          date = d.toISOString().split('T')[0];
        }
        // Accept DD-MM-YYYY and convert to YYYY-MM-DD
        if (/^\d{2}-\d{2}-\d{4}$/.test(date)) {
          const [dd, mm, yyyy] = date.split('-');
          date = yyyy + '-' + mm + '-' + dd;
        }
        // Also accept DD/MM/YYYY
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
          const [dd, mm, yyyy] = date.split('/');
          date = yyyy + '-' + mm + '-' + dd;
        }
        // Validate final date format YYYY-MM-DD
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
        if (!name) return;
        // Map to closest valid category or 'Other'
        const validCat = CATEGORIES.includes(cat) ? cat : 'Other';
        if (!newDays.find(d => d.date === date && d.name === name)) {
          newDays.push({ date, name, cat: validCat, notes });
          added++;
        }
      });
      setMasterDays(newDays); savePersist(newDays, selections, allowances);
      if (added > 0) alert('✅ ' + added + ' day' + (added > 1 ? 's' : '') + ' uploaded!');
      else alert('No valid days found. Check that dates are in DD-MM-YYYY format and Day Name is filled.');
    };

    if (isExcel) {
      const reader = new FileReader();
      reader.onload = ev => {
        const wb = XLSX.read(new Uint8Array(ev.target.result), { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
        // Skip header row
        processRows(rows.slice(1));
      };
      reader.readAsArrayBuffer(file);
    } else {
      // CSV fallback
      const reader = new FileReader();
      reader.onload = ev => {
        const text = ev.target.result.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        const lines = text.split('\n').slice(1);
        const rows = lines.filter(l => l.trim()).map(line => {
          // Simple CSV parse handling quoted fields
          const fields = [];
          let field = '', inQuotes = false;
          for (const ch of line) {
            if (ch === '"') { inQuotes = !inQuotes; }
            else if (ch === ',' && !inQuotes) { fields.push(field); field = ''; }
            else { field += ch; }
          }
          fields.push(field);
          return fields;
        });
        processRows(rows);
      };
      reader.readAsText(file);
    }
    e.target.value = '';
  };

  // ── Client selections ────────────────────────────────────────
  const toggleClientDay = (clientId, day) => {
    const cur    = selections[clientId] || [];
    const max    = allowances[clientId] || 3;
    const prefix = planPrefix(); // current month e.g. "2026-08"
    // Count only THIS month's selections against the allowance
    const curMonth = cur.filter(d => (d.date || '').startsWith(prefix));
    const exists   = cur.find(d => d.date === day.date && d.name === day.name);
    let next;
    if (exists) {
      next = cur.filter(d => !(d.date === day.date && d.name === day.name));
    } else {
      if (curMonth.length >= max) { alert(`${clients.find(c=>c.id===clientId)?.name} can only select ${max} days for ${prefix}.`); return; }
      next = [...cur, day];
    }
    const newSel = { ...selections, [clientId]: next };
    setSelections(newSel);
    savePersist(masterDays, newSel, allowances);
  };

  const buildRequestMsg = () => {
    const prefix = planPrefix();
    const days = monthMasterDays;
    const month = `${MONTHS_LONG[planMonth]} ${planYear}`;
    return `Hi [Client Name],\n\nWe are planning your content calendar for ${month}. Please select your preferred special days using the link that will be generated when you click "Send Request".\n\nYou can pick up to ${allowances[selClient]||3} days from ${days.length} available days.\n\nThank you,\n[Agency Name]`;
  };

  // ── Calendar grid (Step 4) ────────────────────────────────────
  const buildCalGrid = (m, y) => {
    const firstDay = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m+1, 0).getDate();
    const prefix = `${y}-${String(m+1).padStart(2,'0')}`;
    const wos = workOrders.filter(wo => {
      const d = wo.event_date || wo.date || wo.scheduled_date || '';
      if (!d.startsWith(prefix)) return false;
      if (calClient && clientName(wo.client) !== calClient && String(wo.client_id) !== calClient) return false;
      return true;
    });
    const dayMap = {};
    wos.forEach(wo => {
      const d = (wo.event_date||wo.date||wo.scheduled_date||'').split('T')[0];
      if (!dayMap[d]) dayMap[d] = [];
      dayMap[d].push(wo);
    });
    // Special days map
    const specMap = {};
    masterDays.filter(d => d.date.startsWith(prefix)).forEach(d => {
      if (!specMap[d.date]) specMap[d.date] = [];
      specMap[d.date].push(d);
    });
    return { firstDay, daysInMonth, dayMap, specMap, prefix };
  };

  const CalendarGrid = ({ m, y }) => {
    const { firstDay, daysInMonth, dayMap, specMap, prefix } = buildCalGrid(m, y);
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);

    return (
      <div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:'2px',marginBottom:'4px'}}>
          {WEEK_DAYS.map(d=>(
            <div key={d} style={{textAlign:'center',fontSize:'0.72rem',fontWeight:700,color:'var(--text3)',padding:'4px 0',textTransform:'uppercase',letterSpacing:'0.5px'}}>{d}</div>
          ))}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:'3px'}}>
          {cells.map((day, i) => {
            if (!day) return <div key={i} style={{minHeight:'70px'}} />;
            const dateStr = `${prefix}-${String(day).padStart(2,'0')}`;
            const wos = dayMap[dateStr] || [];
            const specs = specMap[dateStr] || [];
            const isToday = dateStr === new Date().toISOString().split('T')[0];
            return (
              <div key={i} style={{
                minHeight:'70px', background:'var(--bg2)', border:`1px solid ${isToday?'var(--accent)':'var(--border)'}`,
                borderRadius:'6px', padding:'4px', position:'relative',
                borderTop: isToday?'3px solid var(--accent)':undefined
              }}>
                <div style={{fontSize:'0.75rem',fontWeight:isToday?800:500,color:isToday?'var(--accent)':'var(--text2)',marginBottom:'3px'}}>{day}</div>
                {specs.map((s,si) => (
                  <div key={si} style={{fontSize:'0.62rem',padding:'1px 4px',borderRadius:'3px',marginBottom:'2px',background:`${CAT_COLORS[s.cat]||'#888'}18`,color:CAT_COLORS[s.cat]||'#888',fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>🎯 {s.name}</div>
                ))}
                {wos.slice(0,2).map((wo,wi) => (
                  <div key={wi} style={{fontSize:'0.62rem',padding:'1px 4px',borderRadius:'3px',marginBottom:'2px',background:'rgba(59,126,240,0.1)',color:'var(--accent)',fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>📋 {clientName(wo.client)}</div>
                ))}
                {wos.length>2 && <div style={{fontSize:'0.6rem',color:'var(--text3)'}}>+{wos.length-2} more</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Step indicator ───────────────────────────────────────────
  const StepBar = () => (
    <div style={{display:'flex',alignItems:'stretch',marginBottom:'20px',background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',overflow:'hidden'}}>
      {[
        {n:1,label:'Special Days Setup'},
        {n:2,label:'Client Requests'},
        {n:3,label:'Review Selections'},
        {n:4,label:'Monthly Planner'},
      ].map((s,i,arr) => (
        <React.Fragment key={s.n}>
          <div onClick={()=>goStep(s.n)} style={{
            display:'flex',alignItems:'center',gap:'8px',padding:'12px 18px',cursor:'pointer',
            flex:1,background:step===s.n?'rgba(59,126,240,0.06)':'transparent',
            color:step===s.n?'var(--accent)':'var(--text2)',
            borderBottom:step===s.n?'3px solid var(--accent)':'3px solid transparent',
            transition:'all 0.15s',
          }}>
            <span style={{
              width:'24px',height:'24px',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',
              fontSize:'0.78rem',fontWeight:800,flexShrink:0,
              background:step===s.n?'var(--accent)':step>s.n?'#1db87a':'var(--bg3)',
              color:step>=s.n?'#fff':'var(--text3)',
            }}>{step>s.n?'✓':s.n}</span>
            <span style={{fontSize:'0.82rem',fontWeight:600,whiteSpace:'nowrap'}}>{s.label}</span>
          </div>
          {i<arr.length-1 && <div style={{display:'flex',alignItems:'center',color:'var(--border2)',fontSize:'1.3rem',padding:'0 2px',flexShrink:0}}>›</div>}
        </React.Fragment>
      ))}
      <div style={{display:'flex',alignItems:'center',color:'var(--border2)',fontSize:'1.3rem',padding:'0 2px',flexShrink:0}}>›</div>
      <div onClick={()=>navigate('/content')} style={{
        display:'flex',alignItems:'center',gap:'8px',padding:'12px 18px',cursor:'pointer',flex:1,
        color:'#6c58f0',fontWeight:600,fontSize:'0.82rem',whiteSpace:'nowrap'
      }}>
        <span style={{width:'24px',height:'24px',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.78rem',fontWeight:800,background:'#6c58f011',color:'#6c58f0',flexShrink:0}}>5</span>
        Content Briefs ↗
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════
  // STEP 1: Special Days Setup
  // ══════════════════════════════════════════════════════════════
  const Step1 = () => (
    <div>
      {/* Top 2-col grid */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px',marginBottom:'16px'}}>
        {/* Planning Month */}
        <div className="card">
          <div className="card-header"><div className="card-title">📅 Planning Month</div></div>
          <div className="form-row" style={{marginBottom:'12px'}}>
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">Month</label>
              <select className="form-select" value={planMonth} onChange={e=>{setPlanMonth(parseInt(e.target.value));setSuggestions([]);setSugChecked({});}}>
                {MONTHS_LONG.map((m,i)=><option key={i} value={i}>{m}</option>)}
              </select>
            </div>
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">Year</label>
              <select className="form-select" value={planYear} onChange={e=>setPlanYear(parseInt(e.target.value))}>
                {[2025,2026,2027,2028].map(y=><option key={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <button className="btn btn-primary" style={{width:'100%'}} onClick={loadSuggestions}>🔄 Load System Suggestions</button>
        </div>

        {/* Bulk Upload */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">📥 Bulk Upload</div>
            <button className="btn btn-secondary" style={{fontSize:'0.72rem',padding:'5px 10px'}} onClick={downloadTemplate}>⬇ Template</button>
          </div>
          <div style={{border:'2px dashed var(--border2)',borderRadius:'var(--radius-sm)',padding:'20px',textAlign:'center',marginBottom:'10px',cursor:'pointer'}}
            onClick={()=>document.getElementById('mp-csv-input').click()}>
            <div style={{fontSize:'1.3rem',marginBottom:'4px'}}>📊</div>
            <div className="text-sm fw-700">Drop Excel or CSV file here or click to browse</div>
            <div className="text-xs text-muted" style={{marginTop:'3px'}}>Date · Day Name · Category · Notes</div>
            <input id="mp-csv-input" type="file" accept=".xlsx,.xls,.csv" style={{display:'none'}} onChange={uploadFile} />
          </div>
          <button className="btn btn-secondary" style={{width:'100%',fontSize:'0.78rem'}} onClick={()=>{
            alert('Expected columns:\n\n1. Date — DD-MM-YYYY (e.g. 02-10-2026)\n2. Day Name — Event name\n3. Category — ' + CATEGORIES.join(', ') + '\n4. Notes — Optional\n\nDownload the template for a ready-to-fill Excel file with category dropdowns.');
          }}>👁 View Format Guide</button>
        </div>
      </div>

      {/* System Suggested Days */}
      <div className="card mb-16">
        <div className="card-header">
          <div className="card-title">💡 System Suggested Days <span className="badge badge-blue">{suggestions.length}</span></div>
          <div className="flex" style={{gap:'8px'}}>
            <button className="btn btn-secondary" style={{fontSize:'0.72rem',padding:'5px 10px'}} onClick={()=>{const c={};suggestions.forEach((_,i)=>c[i]=true);setSugChecked(c);}}>✓ All</button>
            <button className="btn btn-secondary" style={{fontSize:'0.72rem',padding:'5px 10px'}} onClick={()=>setSugChecked({})}>✕ Clear</button>
            <button className="btn btn-primary" style={{fontSize:'0.78rem'}} onClick={addSuggestedDays}>➕ Add Selected to List</button>
          </div>
        </div>
        {suggestions.length === 0 ? (
          <div className="text-sm text-muted" style={{padding:'16px',textAlign:'center'}}>Select a month above and click <b>🔄 Load System Suggestions</b>.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>Date</th><th>Event</th><th>Category</th><th>Note</th><th style={{textAlign:'center'}}>Add</th></tr>
            </thead>
            <tbody>
              {suggestions.map((s,i) => (
                <tr key={i}>
                  <td className="mono fw-700 text-xs">{s.date}</td>
                  <td className="fw-700">{s.name}</td>
                  <td><CatPill cat={s.cat} /></td>
                  <td><input className="form-input" style={{padding:'4px 8px',fontSize:'0.75rem'}} placeholder="Optional" value={sugNotes[i]||''} onChange={e=>setSugNotes(n=>({...n,[i]:e.target.value}))} /></td>
                  <td style={{textAlign:'center'}}>
                    <input type="checkbox" checked={!!sugChecked[i]} onChange={e=>setSugChecked(c=>({...c,[i]:e.target.checked}))} style={{accentColor:'var(--accent)',width:'16px',height:'16px',cursor:'pointer'}} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Custom Day */}
      <AddCustomDay masterDays={masterDays} setMasterDays={setMasterDays} savePersist={savePersist} selections={selections} allowances={allowances} />

      {/* Master Day List */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">
            📋 Master Day List
            <span className="badge badge-green" style={{fontSize:'0.72rem',marginLeft:'8px'}}>{monthMasterDays.length} days · {MONTHS_SHORT[planMonth]} {planYear}</span>
          </div>
          <div className="flex" style={{gap:'8px'}}>
            <button className="btn btn-secondary" style={{fontSize:'0.75rem',color:'var(--red)',borderColor:'var(--red)'}} onClick={resetMonthPlan}>🗑 Reset This Month's Plan</button>
            <button className="btn btn-primary" onClick={()=>goStep(2)}>Next: Client Requests →</button>
          </div>
        </div>
        {monthMasterDays.length === 0 ? (
          <div className="text-sm text-muted" style={{padding:'16px',textAlign:'center'}}>No days added yet.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>Date</th><th>Event / Day</th><th>Category</th><th>Notes</th><th></th></tr>
            </thead>
            <tbody>
              {monthMasterDays.sort((a,b)=>a.date.localeCompare(b.date)).map((d,i) => {
                const realIdx = masterDays.findIndex(x=>x.date===d.date&&x.name===d.name);
                return (
                  <tr key={i}>
                    <td className="mono fw-700 text-xs">{d.date}</td>
                    <td className="fw-700">{d.name}</td>
                    <td><CatPill cat={d.cat} /></td>
                    <td className="text-xs text-muted">{d.notes||'—'}</td>
                    <td><button className="btn btn-red" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>removeMasterDay(realIdx)}>✕</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════
  // STEP 2: Client Requests
  // ══════════════════════════════════════════════════════════════
  const Step2 = () => {
    const msg = buildRequestMsg();

    return (
      <div>
        {/* Month/Year Filter */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 18px',marginBottom:'16px',background:'linear-gradient(135deg,rgba(108,88,240,0.08),rgba(59,126,240,0.06))',border:'1px solid rgba(108,88,240,0.2)',borderRadius:'10px'}}>
          <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
            <span style={{fontSize:'1.3rem'}}>📅</span>
            <div>
              <div className="text-xs text-muted" style={{marginBottom:'2px'}}>Planning for</div>
              <div className="fw-700" style={{fontSize:'1.1rem',color:'var(--accent)'}}>{MONTHS_LONG[planMonth]} {planYear}</div>
            </div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
            <select className="form-select" value={planMonth} onChange={e=>{setPlanMonth(parseInt(e.target.value));setSuggestions([]);setSugChecked({});}} style={{padding:'6px 10px',fontSize:'0.82rem',minWidth:'120px'}}>
              {MONTHS_LONG.map((m,i)=><option key={i} value={i}>{m}</option>)}
            </select>
            <select className="form-select" value={planYear} onChange={e=>setPlanYear(parseInt(e.target.value))} style={{padding:'6px 10px',fontSize:'0.82rem',minWidth:'80px'}}>
              {[2025,2026,2027,2028].map(y=><option key={y}>{y}</option>)}
            </select>
            <span className="badge badge-green" style={{fontSize:'0.72rem',padding:'4px 10px',whiteSpace:'nowrap'}}>{monthMasterDays.length} days</span>
          </div>
        </div>

        {/* Client Allowances */}
        <div className="card mb-16">
          <div className="card-header">
            <div className="card-title">👥 Client Day Allowances</div>
            <button className="btn btn-secondary" style={{fontSize:'0.78rem'}} onClick={()=>goStep(1)}>← Back</button>
          </div>
          <div className="text-xs text-muted mb-12">Set how many special days each client can select this month.</div>
          {clients.length === 0 ? (
            <div className="text-sm text-muted" style={{padding:'12px'}}>No clients found. Add clients in Master Data first.</div>
          ) : (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:'10px',padding:'12px 0'}}>
              {clients.map(c => (
                <div key={c.id} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 12px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)'}}>
                  <div style={{flex:1}}>
                    <div className="fw-700 text-sm">{c.name}</div>
                    <div className="text-xs text-muted">Days allowed</div>
                  </div>
                  <select className="form-select" style={{width:'60px',padding:'4px 6px',fontSize:'0.82rem'}}
                    value={allowances[c.id]||3}
                    onChange={e=>{const a={...allowances,[c.id]:parseInt(e.target.value)};setAllowances(a);savePersist(masterDays,selections,a);}}>
                    {[1,2,3,4,5,6,7,8,9,10].map(n=><option key={n}>{n}</option>)}
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Send Request */}
        <div className="card mb-16">
          <div className="card-header"><div className="card-title">📤 Send Day Selection Request</div></div>
          <div className="form-group" style={{marginBottom:'10px'}}>
            <label className="form-label">Select Clients</label>
            <div style={{background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',padding:'10px 12px',display:'flex',flexWrap:'wrap',gap:'6px',minHeight:'44px'}}>
              {clients.map(c=>(
                <label key={c.id} style={{display:'inline-flex',alignItems:'center',gap:'5px',cursor:'pointer',fontSize:'0.78rem',padding:'3px 8px',background:selectedCl.includes(c.id)?'var(--accent)':'var(--bg2)',color:selectedCl.includes(c.id)?'#fff':'var(--text2)',borderRadius:'20px',border:'1px solid var(--border)'}}>
                  <input type="checkbox" checked={selectedCl.includes(c.id)} onChange={e=>{setSelectedCl(s=>e.target.checked?[...s,c.id]:s.filter(x=>x!==c.id));}} style={{display:'none'}} />
                  {c.name}
                </label>
              ))}
            </div>
            <div className="flex" style={{gap:'8px',marginTop:'6px'}}>
              <button className="btn btn-secondary" style={{fontSize:'0.72rem',padding:'4px 10px'}} onClick={()=>setSelectedCl(clients.map(c=>c.id))}>✓ All</button>
              <button className="btn btn-secondary" style={{fontSize:'0.72rem',padding:'4px 10px'}} onClick={()=>setSelectedCl([])}>✕ Clear</button>
            </div>
          </div>
          <div className="form-row" style={{marginBottom:'10px'}}>
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">Send Via</label>
              <select className="form-select" value={sendVia} onChange={e=>setSendVia(e.target.value)}>
                <option>WhatsApp</option><option>Email</option><option>Copy Text</option>
              </select>
            </div>
            <div className="form-group" style={{margin:0}} />
          </div>
          <div className="form-group">
            <label className="form-label">Message Preview <span style={{fontWeight:400,textTransform:'none',color:'var(--text3)'}}>(editable)</span></label>
            <textarea className="form-textarea" id="mp-request-msg" style={{minHeight:'120px',fontSize:'0.82rem'}} defaultValue={msg} />
          </div>
          <div className="flex" style={{gap:'8px',flexWrap:'wrap'}}>
            <button className="btn btn-primary" onClick={async ()=>{
              if (!selectedCl.length) { alert('Select at least one client.'); return; }
              if (!monthMasterDays.length) { alert('Add days to the master list first.'); return; }
              try {
                const res = await api.post('/day-selections/send', {
                  month: planPrefix(),
                  days: monthMasterDays,
                  allowance: allowances[selectedCl[0]] || 3,
                  client_ids: selectedCl,
                  sent_via: sendVia === 'Copy Text' ? 'WhatsApp' : sendVia,
                });
                const records = res.data?.data || [];
                fetchDaySelReqs(planPrefix());
                // Open messaging for first client
                const rec = records[0];
                if (rec) {
                  const link = window.location.origin + '/select/' + rec.token;
                  const cl = clients.find(c => c.id === rec.client_id);
                  const mnth = `${MONTHS_LONG[planMonth]} ${planYear}`;
                  const finalMsg = `Hi ${cl?.name || 'there'},\n\nWe are planning your content calendar for ${mnth}. Please select your preferred special days using this link:\n\n${link}\n\nYou can pick up to ${rec.allowance} days.\n\nThank you!`;
                  if (sendVia === 'WhatsApp' && cl?.whatsapp) window.open(`https://wa.me/${cl.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(finalMsg)}`, '_blank');
                  else if (sendVia === 'Email' && cl?.email) window.open(`mailto:${cl.email}?subject=${encodeURIComponent('Content Day Selection - ' + mnth)}&body=${encodeURIComponent(finalMsg)}`, '_blank');
                  else { navigator.clipboard.writeText(finalMsg); alert('Link copied to clipboard!'); }
                }
                if (records.length > 1) alert(`Requests sent for ${records.length} clients. Open each client's WhatsApp/Email individually to share their unique link.`);
              } catch (e) { alert(e.response?.data?.message || 'Failed to send request.'); }
            }}>📲 Send Request</button>
            <button className="btn btn-secondary" onClick={async ()=>{
              if (!selectedCl.length) { alert('Select at least one client.'); return; }
              const mnth = `${MONTHS_LONG[planMonth]} ${planYear}`;
              try {
                let allReqs = [...daySelReqs];
                const missingIds = selectedCl.filter(cid => !daySelReqs.find(r => r.client_id === cid)?.token);
                if (missingIds.length > 0) {
                  const res = await api.post('/day-selections/send', {
                    month: planPrefix(),
                    days: monthMasterDays,
                    allowance: allowances[missingIds[0]] || 3,
                    client_ids: missingIds,
                    sent_via: 'WhatsApp',
                  });
                  const newRecs = res.data?.data || [];
                  allReqs = [...allReqs.filter(r => !missingIds.includes(r.client_id)), ...newRecs];
                  fetchDaySelReqs(planPrefix());
                }
                const messages = selectedCl.map(cid => {
                  const cl = clients.find(c => c.id === cid);
                  const req = allReqs.find(r => r.client_id === cid);
                  const link = req?.token ? (window.location.origin + '/select/' + req.token) : '';
                  return `Hi ${cl?.name || 'there'},\n\nWe are planning your content calendar for ${mnth}. Please select your preferred special days using this link:\n\n${link}\n\nYou can pick up to ${req?.allowance || allowances[cid] || 3} days.\n\nThank you!`;
                });
                navigator.clipboard.writeText(messages.join('\n\n---\n\n'));
                alert(`Copied message${messages.length !== 1 ? 's' : ''} for ${messages.length} client${messages.length !== 1 ? 's' : ''}!`);
              } catch (e) { alert(e.response?.data?.message || 'Failed to copy. Please try again.'); }
            }}>📋 Copy Text</button>
          </div>
        </div>

        {/* Record Client Selections */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">✅ Record Client Selections</div>
            <button className="btn btn-primary" style={{fontSize:'0.85rem',fontWeight:700,padding:'8px 22px',background:'#6c58f0',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',boxShadow:'0 4px 14px rgba(108,88,240,0.4)'}} onClick={()=>goStep(3)}>Next: Review →</button>
          </div>
          <div className="text-xs text-muted mb-10">Tick the days each client confirmed. Switch clients using tabs.</div>

          {/* Client tabs */}
          <div style={{display:'flex',gap:'4px',flexWrap:'wrap',marginBottom:'12px',borderBottom:'1px solid var(--border)',paddingBottom:'10px'}}>
            {clients.map(c=>(
              <button key={c.id} onClick={()=>setSelClient(c.id)} style={{
                padding:'5px 14px',fontSize:'0.78rem',fontWeight:600,borderRadius:'6px',cursor:'pointer',
                border:`2px solid ${selClient===c.id?'var(--accent)':'var(--border)'}`,
                background:selClient===c.id?'rgba(59,126,240,0.08)':'var(--bg3)',
                color:selClient===c.id?'var(--accent)':'var(--text2)'
              }}>
                {c.name}
                {(selections[c.id]?.length||0)>0 && <span className="badge badge-blue" style={{fontSize:'0.62rem',marginLeft:'5px'}}>{selections[c.id]?.length}</span>}
                {(() => { const req = daySelReqs.find(r => r.client_id === c.id); if (!req) return null; const colors = { pending: { bg: '#fef3c7', color: '#92400e', label: 'Pending' }, submitted: { bg: '#d1fae5', color: '#065f46', label: 'Submitted' }, agency_filled: { bg: '#dbeafe', color: '#1e40af', label: 'Agency filled' } }; const s = colors[req.status] || colors.pending; return <span style={{ fontSize: '0.6rem', marginLeft: '4px', padding: '1px 6px', borderRadius: '4px', background: s.bg, color: s.color, fontWeight: 600 }}>{s.label}</span>; })()}
              </button>
            ))}
          </div>

          {selClient && (
            <>
              <div style={{background:'rgba(59,126,240,0.06)',border:'1px solid rgba(59,126,240,0.2)',borderRadius:'8px',padding:'10px 14px',marginBottom:'12px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <span className="fw-700 text-sm">{clients.find(c=>c.id===selClient)?.name}</span>
                  <span className="text-xs text-muted" style={{marginLeft:'10px'}}>Allowance: <b style={{color:'var(--accent)'}}>{allowances[selClient]||3} days</b></span>
                </div>
                <span className="text-sm">Picked: <b style={{color:'#1db87a'}}>{(selections[selClient]||[]).filter(d=>(d.date||'').startsWith(planPrefix())).length}</b> / {allowances[selClient]||3}</span>
              </div>
              {monthMasterDays.length === 0 ? (
                <div className="text-sm text-muted" style={{padding:'12px'}}>No days in master list for this month.</div>
              ) : (
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:'8px'}}>
                  {monthMasterDays.sort((a,b)=>a.date.localeCompare(b.date)).map((d,i) => {
                    const checked = !!(selections[selClient]||[]).find(x=>x.date===d.date&&x.name===d.name);
                    return (
                      <label key={i} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 12px',background:checked?'rgba(59,126,240,0.06)':'var(--bg3)',border:`1px solid ${checked?'var(--accent)':'var(--border)'}`,borderRadius:'var(--radius-sm)',cursor:'pointer'}}>
                        <input type="checkbox" checked={checked} onChange={()=>toggleClientDay(selClient,d)} style={{accentColor:'var(--accent)',width:'15px',height:'15px',flexShrink:0}} />
                        <div>
                          <div className="fw-700 text-sm">{d.name}</div>
                          <div className="text-xs text-muted">{d.date} · <CatPill cat={d.cat} /></div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // ══════════════════════════════════════════════════════════════
  // STEP 3: Review Selections
  // ══════════════════════════════════════════════════════════════
  // ── Generate All Calendars ───────────────────────────────────
  const GenerateCalendarsBtn = () => {
    const [generating, setGenerating] = useState(false);
    const [result,     setResult]     = useState(null);

    const generate = async () => {
      setGenerating(true);
      setResult(null);
      let added = 0, skipped = 0, failed = 0;
      try {
        // Get existing WOs to avoid duplicates
        const existingR = await api.get('/work-orders');
        const existing  = existingR.data?.data || existingR.data || [];

        for (const client of clients) {
          const clientName_ = client.name || clientName(client);
          const clientSel   = selections[client.id] || [];
          if (!clientSel.length) continue;

          for (const day of clientSel) {
            // Skip if already exists for this client+date
            const dup = existing.find(wo =>
              clientName(wo.client) === clientName_ &&
              (wo.event_date||wo.date||'') === day.date
            );
            if (dup) { skipped++; continue; }

            try {
              await api.post('/work-orders', {
                client_id:  client.id,
                client:     clientName_,
                event_date: day.date,
                title:      day.name,
                day_name:   day.name,
                category:   day.cat,
                notes:      `Draft content for ${day.name}`,
                status:     'Draft',
                platforms:  [],
                output_formats: [],
              });
              added++;
            } catch(e) { failed++; }
          }
        }
        setResult({ added, skipped, failed });
        if (added > 0 || skipped > 0) goStep(4);
      } catch(e) {
        alert('Error: ' + e.message);
      } finally {
        setGenerating(false);
      }
    };

    return (
      <div>
        <div className="flex" style={{gap:'8px',alignItems:'center',flexWrap:'wrap'}}>
          <button className="btn btn-primary" onClick={generate} disabled={generating}>
            {generating ? '⏳ Generating...' : '⚡ Generate All Calendars'}
          </button>
          <button className="btn btn-secondary" onClick={()=>goStep(4)}>View Calendars →</button>
          {generating && <span className="text-xs text-muted">Creating Work Orders for confirmed selections...</span>}
        </div>
        {result && (
          <div style={{marginTop:'12px',padding:'10px 14px',borderRadius:'var(--radius-sm)',background:result.added>0?'rgba(29,184,122,0.08)':'var(--bg3)',border:`1px solid ${result.added>0?'rgba(29,184,122,0.3)':'var(--border)'}`}}>
            <span style={{fontWeight:700,color:result.added>0?'#1db87a':'var(--text2)'}}>
              {result.added>0?'✅':'ℹ️'} {result.added} entries created
            </span>
            {result.skipped>0 && <span className="text-xs text-muted" style={{marginLeft:'12px'}}>· {result.skipped} already exist (skipped)</span>}
            {result.failed>0  && <span className="text-xs" style={{marginLeft:'12px',color:'#e84545'}}>· {result.failed} failed</span>}
            {result.added>0 && <button onClick={()=>goStep(4)} style={{marginLeft:'12px',padding:'3px 10px',fontSize:'0.75rem',fontWeight:700,background:'var(--accent)',color:'#fff',border:'none',borderRadius:'6px',cursor:'pointer'}}>→ View in Step 4</button>}
          </div>
        )}
      </div>
    );
  };

  const Step3 = () => (
    <div>
      <div className="card mb-16">
        <div className="card-header">
          <div className="card-title">📊 All Client Selections</div>
          <button className="btn btn-secondary" style={{fontSize:'0.78rem'}} onClick={()=>goStep(2)}>← Back</button>
        </div>
        {clients.length === 0 ? (
          <div className="text-sm text-muted" style={{padding:'16px'}}>No clients found.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>Client</th><th>Selected Days</th><th>Count</th></tr>
            </thead>
            <tbody>
              {clients.map(c => {
                const sel = selections[c.id] || [];
                return (
                  <tr key={c.id}>
                    <td className="fw-700">{c.name}</td>
                    <td>
                      {sel.length===0 ? <span className="text-xs text-muted">None selected</span> : (
                        <div style={{display:'flex',flexWrap:'wrap',gap:'4px'}}>
                          {sel.sort((a,b)=>a.date.localeCompare(b.date)).map((d,i)=>(
                            <span key={i} style={{display:'inline-flex',alignItems:'center',gap:'4px',padding:'2px 8px',background:'rgba(59,126,240,0.08)',border:'1px solid rgba(59,126,240,0.2)',borderRadius:'20px',fontSize:'0.7rem',fontWeight:600,color:'var(--accent)'}}>
                              {d.date} — {d.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td><span className={`badge ${sel.length>0?'badge-green':'badge-gray'}`}>{sel.length} / {allowances[c.id]||3}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">🚀 Generate Content Briefs</div>
        </div>
        <p className="text-sm text-muted" style={{marginBottom:'20px',lineHeight:'1.6'}}>Auto-generate draft Work Orders for all clients with confirmed special day selections. Skips duplicates already in the system.</p>
        <GenerateCalendarsBtn />
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════
  // STEP 4: Monthly Planner
  // ══════════════════════════════════════════════════════════════
  // ── Platform metadata ─────────────────────────────────────────
  const PLAT_META = {
    'Instagram':   { color:'#e8323c', icon:'📸' }, 'Facebook':  { color:'#1877f2', icon:'👥' },
    'YouTube':     { color:'#ff0000', icon:'▶️' },  'LinkedIn':  { color:'#0077b5', icon:'💼' },
    'X / Twitter': { color:'#1da1f2', icon:'🐦' },  'WhatsApp':  { color:'#25d366', icon:'💬' },
    'Google Ads':  { color:'#4285f4', icon:'🎯' },  'Meta Ads':  { color:'#0668e1', icon:'📘' },
    'Pinterest':   { color:'#bd081c', icon:'📌' },  'TikTok':    { color:'#010101', icon:'🎵' },
    'Snapchat':    { color:'#fffc00', icon:'👻' },  'Telegram':  { color:'#2ca5e0', icon:'✈️' },
  };
  const getPlatMeta = (n) => PLAT_META[n] || { color:'#6b7280', icon:'📱' };

  const getWorkflowPos = (wo) => {
    const st = wo.cached_status || wo.status || '';
    if (st==='Active'||st==='In Progress') return { label:'⚡ Active',    color:'#9050e8', bg:'rgba(144,80,232,0.12)', border:'#9050e888' };
    if (st==='Done')      return { label:'✅ Done',      color:'#1db87a', bg:'rgba(29,184,122,0.12)', border:'#1db87a88' };
    if (st==='On Hold')   return { label:'⏸ On Hold',   color:'#e87830', bg:'rgba(232,120,48,0.14)', border:'#e8783088' };
    if (st==='Cancelled') return { label:'❌ Cancelled', color:'var(--text3)', bg:'var(--bg3)',        border:'var(--border)' };
    return { label:'📋 Planning', color:'var(--text2)', bg:'var(--bg3)', border:'var(--border2)' };
  };

  // ── Add / Edit Entry Modal ────────────────────────────────────
  // ── EntryModal — full blueprint match ─────────────────────
  // ── EntryModal ─────────────────────────────────────────────
  const EntryModal = ({ mode, entry, prefillDate, platforms, campaignTypes, outputFormats, roles, teamMembers, weeklyOff, holidays, leadDays, onSave, onClose }) => {
    const isEdit = mode === 'edit';
    const FAVES_KEY = 'workops_ofFavorites';
    const parseArr = (v) => { if (!v) return []; if (Array.isArray(v)) return v; try { return JSON.parse(v); } catch(e) { return []; } };

    // ── Platform meta — SVG logo + color from master or fallback ──
    const getPlatMeta = (name) => {
      const p = (platforms||[]).find(x=>x.name===name);
      const color = p?.color || PLATFORM_COLORS[name] || '#6b7280';
      return { color, svg: getPlatformSvg(name), icon: p?.icon||'📱' };
    };

    // ── Client helpers ──────────────────────────────────────
    const getClientPlatforms = (cn) => {
      const c = clients.find(x=>clientName(x)===cn);
      return c ? parseArr(c.platforms) : [];
    };
    const getClientTeamRoles = (cn) => {
      const c = clients.find(x=>clientName(x)===cn);
      if (!c?.team_assignments) return {};
      try { return typeof c.team_assignments==='string' ? JSON.parse(c.team_assignments) : c.team_assignments; } catch(e){ return {}; }
    };

    // ── Scheduling date — N working days before event (from tenant settings) ───────
    // Respects weekly off days from tenant settings + holidays from master data
    const calcSchedDate = (eventDate) => {
      if (!eventDate) return '';
      // Build holiday date set — handle recurring holidays (match MM-DD regardless of year)
      const year = new Date(eventDate+'T00:00:00').getFullYear();
      const holidaySet = new Set(
        (holidays||[]).map(h => {
          if (!h.date) return null;
          const d = h.date.slice(0,10); // yyyy-mm-dd
          if (h.recurring === 'yes' || h.recurring === true || h.recurring === 1) {
            // Match any year — store as MM-DD
            return d.slice(5); // mm-dd
          }
          return d; // exact date
        }).filter(Boolean)
      );
      const isHoliday = (d) => {
        const ds = d.toISOString().split('T')[0];
        const mmdd = ds.slice(5);
        return holidaySet.has(ds) || holidaySet.has(mmdd);
      };
      // Weekly off days from tenant (0=Sun…6=Sat), fallback to [0,6]
      const offDays = new Set(weeklyOff && weeklyOff.length ? weeklyOff : [0, 6]);
      const isWorkingDay = (d) => !offDays.has(d.getDay()) && !isHoliday(d);

      const d = new Date(eventDate+'T00:00:00');
      let rem = leadDays || 3;
      let safety = 60; // prevent infinite loop
      while (rem > 0 && safety-- > 0) {
        d.setDate(d.getDate() - 1);
        if (isWorkingDay(d)) rem--;
      }
      return d.toISOString().split('T')[0];
    };

    // ── Initial form values ──────────────────────────────────
    const savedPlats = isEdit ? parseArr(entry?.platforms) : [];
    const fmtKeyFn = (f) => f && f.platform ? `${f.platform} ${f.name||''}` : (f?.name||'');
    const savedFmts  = isEdit ? (() => {
      const raw = parseArr(entry?.output_formats||entry?.outputFormats);
      return raw.map(v => {
        if (typeof v === 'number' || (typeof v === 'string' && /^\d+$/.test(v))) {
          // Legacy ID → find format → platform+name
          const found = (outputFormats||[]).find(f => String(f.id) === String(v));
          return found ? fmtKeyFn(found) : null;
        }
        // Check if already a platform+name key
        const exact = (outputFormats||[]).find(f => fmtKeyFn(f) === v);
        if (exact) return v;
        // Legacy name-only → find by name → platform+name
        const byName = (outputFormats||[]).find(f => (f.name||'') === v);
        return byName ? fmtKeyFn(byName) : v;
      }).filter(Boolean);
    })() : [];
    const cn0        = isEdit ? clientName(entry?.client) : (clients[0]?.name||'');
    const defPlats   = savedPlats.length ? savedPlats : getClientPlatforms(cn0);
    const defDate    = isEdit ? (entry?.event_date||entry?.date||'') : (prefillDate||'');
    const defSched   = isEdit ? (entry?.scheduling_date || (defDate ? calcSchedDate(defDate) : '')) : (prefillDate ? calcSchedDate(prefillDate) : '');

    const [form, setForm] = useState({
      client:        cn0,
      date:          defDate,
      dayName:       isEdit ? (entry?.title||entry?.day_name||'') : '',
      category:      isEdit ? (typeof entry?.category==='object'?entry.category.name:entry?.category||'Festival') : 'Festival',
      campaignType:  String(isEdit?(entry?.campaign_type_id||entry?.campaign_type?.id||''):''),
      schedulingDate:defSched,
      selPlatforms:  defPlats,
      selFormats:    savedFmts,
      teamRoles:     isEdit ? (() => {
        const saved = entry?.team_roles;
        const parsed = saved && typeof saved==='string' ? (() => { try{return JSON.parse(saved);}catch(e){return null;} })() : (saved && typeof saved==='object' ? saved : null);
        return (parsed && Object.keys(parsed).length) ? parsed : getClientTeamRoles(entry?.client);
      })() : getClientTeamRoles(cn0),
      notes:         isEdit ? (entry?.notes||'') : '',
    });

    const [ofSearch, setOfSearch]   = useState('');
    const [saving,   setSaving]     = useState(false);
    const [favorites,setFavorites]  = useState(() => { try{ return JSON.parse(localStorage.getItem(FAVES_KEY)||'[]'); }catch(e){return[];} });

    // ── Draggable modal ──────────────────────────────────────
    const [dragPos, setDragPos] = useState(null); // null = centered, {x,y} = dragged
    const dragRef = React.useRef(null);
    const startDrag = (e) => {
      if (e.button !== 0) return;
      const el = dragRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const offX = e.clientX - rect.left;
      const offY = e.clientY - rect.top;
      const onMove = (mv) => setDragPos({ x: mv.clientX - offX, y: mv.clientY - offY });
      const onUp   = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup',   onUp);
    };

    // ── Toggle favorite ──────────────────────────────────────
    const toggleFav = (fmtId, e) => {
      e.stopPropagation(); e.preventDefault();
      setFavorites(prev => {
        const next = prev.includes(fmtId) ? prev.filter(x=>x!==fmtId) : [...prev, fmtId];
        localStorage.setItem(FAVES_KEY, JSON.stringify(next));
        return next;
      });
    };

    // ── When client changes — pre-fill platforms & team ──────
    const handleClientChange = (newCn) => {
      setForm(f => ({...f, client:newCn, selPlatforms:getClientPlatforms(newCn), teamRoles:getClientTeamRoles(newCn)}));
    };

    // ── Auto-calc scheduling date when event date changes ────
    const handleDateChange = (newDate) => {
      const sd = calcSchedDate(newDate);
      setForm(f => ({...f, date:newDate, schedulingDate:sd}));
    };

    const togglePlatform = (name) => setForm(f=>({...f,selPlatforms:f.selPlatforms.includes(name)?f.selPlatforms.filter(x=>x!==name):[...f.selPlatforms,name]}));
    const fmtKey = (f) => f.platform ? `${f.platform} ${f.name}` : f.name;
    const toggleFormat = (key) => setForm(f=>({...f,selFormats: f.selFormats.includes(key)?f.selFormats.filter(x=>x!==key):[...f.selFormats,key]}));

    // ── Output formats grouped by Content Type ───────────────
    const filteredOF = (outputFormats||[]).filter(f => f.enabled!==false && (!ofSearch ||
      f.name?.toLowerCase().includes(ofSearch.toLowerCase()) ||
      f.platform?.toLowerCase().includes(ofSearch.toLowerCase()) ||
      (f.type||'').toLowerCase().includes(ofSearch.toLowerCase())
    ));
    const favOFs = filteredOF.filter(f=>favorites.includes(f.id));
    const ofGroups = {};
    filteredOF.forEach(f=>{ const g=f.type||f.platform||'Other'; (ofGroups[g]=ofGroups[g]||[]).push(f); });

    const FormatItem = ({f}) => {
      const checked = form.selFormats.includes(fmtKey(f));
      const isFav   = favorites.includes(f.id);
      const pc      = getPlatMeta(f.platform).color;
      const dims    = f.width&&f.height&&f.width!=='Custom'?`${f.width}×${f.height}`:'';
      return (
        <div style={{display:'flex',alignItems:'center',gap:'8px',padding:'5px 8px',borderRadius:'6px',cursor:'pointer',background:checked?`${pc}08`:undefined}}
          onClick={()=>toggleFormat(fmtKey(f))}>
          <input type="checkbox" checked={checked} onChange={()=>{}} style={{accentColor:pc,width:'14px',height:'14px',flexShrink:0,pointerEvents:'none'}} />
          <span style={{flex:1,minWidth:0}}>
            <span style={{fontSize:'0.7rem',fontWeight:700,color:pc}}>{f.platform} </span>
            <span style={{fontSize:'0.78rem',fontWeight:checked?700:400}}>{f.name}</span>
            {dims&&<span style={{fontSize:'0.62rem',color:'var(--text3)',marginLeft:'4px'}}>{dims}</span>}
          </span>
          <span onClick={e=>toggleFav(f.id,e)} title={isFav?'Remove from favorites':'Add to favorites'}
            style={{cursor:'pointer',fontSize:'0.88rem',color:isFav?'#f7c34f':'var(--text3)',flexShrink:0}}>
            {isFav?'★':'☆'}
          </span>
        </div>
      );
    };

    // ── Save ─────────────────────────────────────────────────
    const save = async () => {
      if (!form.client||!form.date||!form.dayName.trim()) { alert('Client, date and event name required'); return; }
      setSaving(true);
      try {
        const clientObj = clients.find(c=>clientName(c)===form.client);
        const clientId  = clientObj?.id || null;
        if (!clientId && !isEdit) { alert('Client not found.'); setSaving(false); return; }
        const payload = {
          ...(clientId&&{client_id:clientId}),
          client:           form.client,
          event_date:       form.date,
          title:            form.dayName,
          day_name:         form.dayName,
          category:         form.category,
          campaign_type_id: form.campaignType?parseInt(form.campaignType):null,
          scheduling_date:  form.schedulingDate||null,
          platforms:        form.selPlatforms,
          output_formats:   form.selFormats,
          notes:            form.notes,
          team_roles:       form.teamRoles||{},
        };
        if (isEdit) await api.put('/work-orders/'+entry.id, payload);
        else        await api.post('/work-orders', payload);
        onSave(); onClose();
      } catch(e) { alert('Save failed: '+(e.response?.data?.message||e.message)); }
      finally { setSaving(false); }
    };

    const st     = isEdit ? (entry?.cached_status||entry?.status||'') : '';
    const isHold = st==='On Hold';
    const isActive= st==='Active'||st==='In Progress';
    const dayDiff = form.date&&form.schedulingDate ? Math.max(0,Math.ceil((new Date(form.date)-new Date(form.schedulingDate))/(1000*60*60*24))) : null;

    return (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:9999,display:dragPos?'block':'flex',alignItems:'center',justifyContent:'center'}} onClick={e=>e.target===e.currentTarget&&!dragPos&&onClose()}>
        <div ref={dragRef} style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',boxShadow:'0 20px 60px rgba(0,0,0,0.3)',width:'660px',maxWidth:'96vw',maxHeight:'92vh',display:'flex',flexDirection:'column',position:dragPos?'fixed':'relative',left:dragPos?dragPos.x:undefined,top:dragPos?dragPos.y:undefined,userSelect:'none'}}>

          {/* Header */}
          <div onMouseDown={startDrag} style={{padding:'14px 20px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'flex-start',position:'sticky',top:0,background:'var(--bg2)',zIndex:1,cursor:'grab'}}>
            <div>
              <div className="fw-700">{isEdit?'✏️ Edit Entry':'+ Add Calendar Entry'}</div>
              {isEdit && (
                <div style={{display:'flex',alignItems:'center',gap:'8px',marginTop:'4px',flexWrap:'wrap'}}>
                  <span className="mono" style={{fontSize:'0.72rem',color:'var(--accent)',fontWeight:700}}>{entry.wo_number||`WO-${entry.id}`}</span>
                  <span style={{color:'var(--border2)'}}>·</span>
                  <span style={{fontSize:'0.78rem',fontWeight:600}}>🏢 {clientName(entry.client)}</span>
                  {isHold&&<span style={{display:'inline-flex',alignItems:'center',gap:'5px',background:'#e8783018',border:'1px solid #e8783044',borderRadius:'20px',padding:'2px 10px',fontSize:'0.72rem',fontWeight:700,color:'#e87830'}}>⏸ On Hold{entry.hold_reason?` — ${entry.hold_reason}`:''}</span>}
                  {isActive&&<span style={{display:'inline-flex',alignItems:'center',gap:'4px',background:'#6c58f018',border:'1px solid #6c58f044',borderRadius:'20px',padding:'2px 10px',fontSize:'0.72rem',fontWeight:700,color:'#6c58f0'}}>⚡ Active</span>}
                </div>
              )}
            </div>
            <button onClick={onClose} style={{background:'none',border:'none',fontSize:'1.1rem',cursor:'pointer',color:'var(--text3)'}}>✕</button>
          </div>

          {/* Body */}
          <div style={{overflowY:'auto',flex:1,padding:'18px 20px',display:'flex',flexDirection:'column',gap:'14px'}}>

            {/* Client + Date */}
            <div className="form-row">
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">Client</label>
                <select className="form-select" value={form.client} onChange={e=>handleClientChange(e.target.value)}>
                  <option value="">— Select —</option>
                  {clients.filter(c=>c.is_active!==false).map(c=>{const n=clientName(c);return<option key={c.id} value={n}>{n}</option>;})}
                </select>
              </div>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">Event Date</label>
                <input type="date" className="form-input" value={form.date} onChange={e=>handleDateChange(e.target.value)} />
              </div>
            </div>

            {/* Event Name + Category */}
            <div className="form-row">
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">Event / Day Name</label>
                <input className="form-input" placeholder="e.g. Diwali Campaign" value={form.dayName} onChange={e=>setForm(f=>({...f,dayName:e.target.value}))} />
              </div>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">Category</label>
                <select className="form-select" value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                  {CATEGORIES.map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Workflow Type */}
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">🎯 Workflow Type</label>
              <select className="form-select" value={form.campaignType} onChange={e=>setForm(f=>({...f,campaignType:e.target.value}))}>
                <option value="">— Select Campaign Type —</option>
                {(campaignTypes||[]).map(ct=><option key={ct.id} value={String(ct.id)}>{ct.name}</option>)}
              </select>
            </div>

            {/* Scheduling Date */}
            <div className="form-row">
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">📅 Scheduling Date <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>— auto: {leadDays||3} working days before event</span></label>
                <input type="date" className="form-input" value={form.schedulingDate} onChange={e=>setForm(f=>({...f,schedulingDate:e.target.value}))} />
              </div>
              <div style={{marginTop:'22px',padding:'8px 10px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',fontSize:'0.75rem',color:'var(--text3)',flex:1,minHeight:'38px',display:'flex',alignItems:'center'}}>
                {form.schedulingDate && form.date
                  ? <span style={{color:'var(--accent)',fontWeight:600}}>📅 Scheduling/Publishing: {form.schedulingDate.split('-').reverse().join('-')} ({leadDays||3} working day{(leadDays||3)!==1?'s':''} before {form.date.split('-').reverse().join('-')})</span>
                  : <span style={{color:'var(--text3)'}}>Select event date to auto-calculate</span>}
              </div>
            </div>

            {/* Platforms — from Master Data, icon from platform.icon */}
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">📱 Platforms <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>— pre-filled from client master · adjust as needed</span></label>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'6px',padding:'10px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)'}}>
                {(platforms||[]).filter(p=>p.enabled!==false&&p.is_active!==false).map(p=>{
                  const meta=getPlatMeta(p.name); const checked=form.selPlatforms.includes(p.name);
                  return (
                    <label key={p.id}
                      style={{display:'flex',alignItems:'center',gap:'7px',padding:'6px 8px',border:`1.5px solid ${checked?meta.color:'var(--border)'}`,borderRadius:'7px',fontSize:'0.75rem',cursor:'pointer',background:checked?`${meta.color}0f`:'var(--bg2)',color:checked?meta.color:'var(--text)',fontWeight:checked?700:400,transition:'all 0.12s',userSelect:'none'}}>
                      <input type="checkbox" checked={checked} onChange={()=>togglePlatform(p.name)}
                        style={{accentColor:meta.color,width:'14px',height:'14px',flexShrink:0}} />
                      {meta.svg
                        ? <span style={{width:'18px',height:'18px',display:'inline-flex',alignItems:'center',justifyContent:'center',flexShrink:0,borderRadius:'3px',overflow:'hidden'}} dangerouslySetInnerHTML={{__html:meta.svg}} />
                        : <span style={{fontSize:'0.95rem',lineHeight:1,flexShrink:0}}>{meta.icon}</span>
                      }
                      <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</span>
                    </label>
                  );
                })}
                {!(platforms||[]).filter(p=>p.enabled!==false).length && <span className="text-xs text-muted">No platforms in Master Data → add them in Master Data → Platforms</span>}
              </div>
            </div>

            {/* Output Formats — grouped by content type, with Favorites */}
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">📐 Output Formats <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>— search and select · ☆ to favorite</span></label>
              <div style={{background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',overflow:'hidden'}}>
                {/* Search bar */}
                <div style={{padding:'8px 10px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:'8px',background:'var(--bg2)'}}>
                  <span style={{color:'var(--text3)'}}>🔍</span>
                  <input placeholder="Search formats... e.g. Reel, Story, 1080, Square"
                    value={ofSearch} onChange={e=>setOfSearch(e.target.value)}
                    style={{border:'none',background:'transparent',outline:'none',fontSize:'0.82rem',color:'var(--text)',flex:1}} />
                  {ofSearch&&<button onClick={()=>setOfSearch('')} style={{background:'none',border:'none',color:'var(--text3)',cursor:'pointer',fontSize:'0.9rem'}}>✕</button>}
                </div>
                {/* List */}
                <div style={{maxHeight:'240px',overflowY:'auto',padding:'6px 8px'}}>
                  {/* Favorites pane */}
                  {favOFs.length>0&&(
                    <div style={{marginBottom:'10px'}}>
                      <div style={{fontSize:'0.72rem',fontWeight:700,color:'#f7c34f',padding:'4px 0',borderBottom:'1px solid var(--border)',marginBottom:'4px',display:'flex',alignItems:'center',gap:'5px'}}>
                        ★ Favorites <span style={{fontWeight:400,color:'var(--text3)'}}>(click ☆ to remove)</span>
                      </div>
                      <div style={{background:'rgba(247,195,79,0.06)',border:'1px solid rgba(247,195,79,0.2)',borderRadius:'8px',padding:'4px 6px'}}>
                        {favOFs.map(f=><FormatItem key={`fav-${f.id}`} f={f} />)}
                      </div>
                    </div>
                  )}
                  {favOFs.length===0&&!ofSearch&&(
                    <div style={{fontSize:'0.72rem',color:'var(--text3)',marginBottom:'8px',padding:'2px 6px'}}>★ Click ☆ next to any format to add to Favorites for quick access.</div>
                  )}
                  {/* Grouped by content type */}
                  {Object.entries(ofGroups).map(([grpName,fmts])=>(
                    <div key={grpName} style={{marginBottom:'10px'}}>
                      <div style={{fontSize:'0.72rem',fontWeight:700,color:'var(--text3)',padding:'4px 0',borderBottom:'1px solid var(--border)',marginBottom:'3px',display:'flex',alignItems:'center',gap:'5px'}}>
                        📦 {grpName} <span style={{fontWeight:400}}>({fmts.length})</span>
                      </div>
                      {fmts.map(f=><FormatItem key={f.id} f={f} />)}
                    </div>
                  ))}
                  {!Object.keys(ofGroups).length&&<div className="text-xs text-muted" style={{padding:'12px'}}>No formats match.</div>}
                </div>
                {/* Footer */}
                <div style={{padding:'6px 10px',borderTop:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',background:'var(--bg2)'}}>
                  <span className="text-xs text-muted">{form.selFormats.length} format{form.selFormats.length!==1?'s':''} selected</span>
                  <button onClick={()=>setForm(f=>({...f,selFormats:[]}))} style={{background:'none',border:'none',fontSize:'0.72rem',color:'var(--text3)',cursor:'pointer'}}>Clear all</button>
                </div>
              </div>
            </div>

            {/* Team Role Assignments */}
            {(roles||[]).filter(r=>r.enabled!==false).length>0&&(
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">👥 Team — Role-wise Assignment <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>— pre-filled from client master</span></label>
                <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                  {(roles||[]).filter(r=>r.enabled!==false).map(r=>{
                    const activeM=(teamMembers||[]).filter(m=>{ const arr=parseArr(m.roles_list||m.roles||(m.role?[m.role]:[])); return arr.includes(r.name); });
                    const allM=activeM.length?activeM:(teamMembers||[]);
                    const clientObj=clients.find(c=>clientName(c)===form.client);
                    const ta=(()=>{ try{ return typeof clientObj?.team_assignments==='string'?JSON.parse(clientObj.team_assignments):clientObj?.team_assignments||{}; }catch(e){return{};} })();
                    const defId=ta[r.id]||ta[String(r.id)]||'';
                    const curVal=String(form.teamRoles[r.id]||form.teamRoles[String(r.id)]||defId);
                    return (
                      <div key={r.id} style={{display:'grid',gridTemplateColumns:'160px 1fr',gap:'8px',alignItems:'center',padding:'5px 8px',background:'var(--bg3)',borderRadius:'6px'}}>
                        <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                          <div style={{width:'8px',height:'8px',borderRadius:'50%',background:r.color||'var(--accent)',flexShrink:0}} />
                          <span style={{fontSize:'0.78rem',fontWeight:600,color:r.color||'var(--accent)'}}>{r.name}</span>
                        </div>
                        <select className="form-select" style={{fontSize:'0.78rem',padding:'4px 8px'}} value={curVal}
                          onChange={e=>setForm(f=>({...f,teamRoles:{...f.teamRoles,[r.id]:e.target.value?parseInt(e.target.value):''}}))}>
                          <option value="">— Not assigned —</option>
                          {allM.map(m=>{ const av=m.availability==='available'?'🟢':m.availability==='busy'?'🟡':'🔴'; return <option key={m.id} value={String(m.id)}>{av} {m.name}</option>; })}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">Notes</label>
              <textarea className="form-textarea" style={{height:'70px'}} placeholder="Content brief, special instructions..."
                value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} />
            </div>
          </div>

          {/* Footer */}
          <div style={{padding:'12px 20px',borderTop:'1px solid var(--border)',display:'flex',gap:'8px',justifyContent:'flex-end',position:'sticky',bottom:0,background:'var(--bg2)'}}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(isEdit?'Update Entry':'Save Entry')}</button>
          </div>
        </div>
      </div>
    );
  };



  const Step4 = ({ platforms, campaignTypes, outputFormats, roles, teamMembers, weeklyOff, holidays, leadDays }) => {
    const [lcM,    setLcM]    = useState(now.getMonth());
    const [lcY,    setLcY]    = useState(now.getFullYear());
    const [lcAll,  setLcAll]  = useState(true);
    const [lcCl,   setLcCl]   = useState('');
    const [emM,    setEmM]    = useState(now.getMonth());
    const [emY,    setEmY]    = useState(now.getFullYear());
    const [emAll,  setEmAll]  = useState(true);
    const [emMonths, setEmMonths] = useState([]); // [] = all months, else array of month indices
    const [emMonthOpen, setEmMonthOpen] = useState(false);
    const [emWoSearch, setEmWoSearch] = useState('');
    const [selIds, setSelIds] = useState(new Set());
    const [modal,  setModal]  = useState(null);
    const [bulkModal, setBulkModal] = useState(null); // 'workflow'|'formats'|'platforms'
    const [bulkWFType, setBulkWFType] = useState('');
    const [bulkFormats, setBulkFormats] = useState([]);
    const [bulkPlatforms, setBulkPlatforms] = useState([]);
    const [bulkPlatformMode, setBulkPlatformMode] = useState('replace');
    const [bulkFormatSearch, setBulkFormatSearch] = useState('');
    const [localWOs, setLocalWOs] = useState(workOrders);

    const CCOLORS = ['#3b7ef0','#1db87a','#e87830','#6c58f0','#e84545','#9050e8','#18b8b8','#e83898','#f7c34f'];
    const uniqueClients = [...new Set(localWOs.map(w=>clientName(w.client)).filter(Boolean))];
    const clientColors = {};
    uniqueClients.forEach((c,i) => { clientColors[c] = CCOLORS[i%CCOLORS.length]; });
    const todayStr = new Date().toISOString().split('T')[0];

    const getStatus = (wo) => wo.cached_status || wo.status || '';

    const refreshWOs = useCallback(async () => {
      try {
        const r = await api.get('/work-orders');
        const data = r.data?.data || r.data || [];
        setLocalWOs(data);
        // Note: do NOT call setWorkOrders here — causes parent re-render → Step4 remount loop
      } catch(e){}
    }, []);

    useEffect(() => { refreshWOs(); }, []);
    // Sync from parent only on first mount, not on every workOrders change
    // eslint-disable-next-line react-hooks/exhaustive-deps

    const setCalMonth = (m,y) => { setLcM(m); setLcY(y); setEmM(m); setEmY(y); };

    const goEntryMonth = (dir) => {
      // Always move 1 month at a time in Calendar Entries
      if (dir==='current') { const n=new Date(); setEmM(n.getMonth()); setEmY(n.getFullYear()); setEmMonths([n.getMonth()]); return; }
      let m=emM, y=emY;
      if (dir==='prev') { m--; if(m<0){m=11;y--;} }
      if (dir==='next') { m++; if(m>11){m=0;y++;} }
      setEmM(m); setEmY(y); setEmMonths([m]);
    };

    const toggleAll = () => { setLcAll(v=>!v); setEmAll(v=>!v); };

    // ── Workflow position — matches blueprint mpWorkflowPosition ──
    const workflowPos = (wo) => {
      const st = getStatus(wo);
      if (st==='On Hold')   return { label:'⏸ On Hold',    color:'#e87830', bg:'rgba(232,120,48,0.14)',  border:'#e8783088' };
      if (st==='Cancelled') return { label:'❌ Cancelled',  color:'var(--text3)', bg:'var(--bg3)',          border:'var(--border)' };
      if (st==='Done')      return { label:'✅ Done',       color:'#1db87a', bg:'rgba(29,184,122,0.12)',  border:'#1db87a88' };
      if (st==='Active'||st==='In Progress') return { label:'⚡ Active', color:'#9050e8', bg:'rgba(144,80,232,0.12)', border:'#9050e888' };
      return { label:'📋 Planning', color:'var(--text2)', bg:'var(--bg3)', border:'var(--border2)' };
    };

    // ── Filter WOs ─────────────────────────────────────────────
    const filterWOs = () => localWOs.filter(wo => {
      const d = wo.event_date||wo.date||wo.scheduled_date||'';
      if (!d) return false;
      if (emMonths.length > 0) {
        // Filter: must match emY and one of the selected months
        const matches = emMonths.some(m =>
          d.startsWith(`${emY}-${String(m+1).padStart(2,'0')}`)
        );
        if (!matches) return false;
      } else {
        // No month filter — show all entries for the year
        if (!d.startsWith(`${emY}`)) return false;
      }
      if (lcCl && clientName(wo.client)!==lcCl) return false;
      if (emWoSearch && !(wo.wo_number||'WO-'+wo.id).toLowerCase().includes(emWoSearch.toLowerCase()) && !(wo.title||wo.day_name||'').toLowerCase().includes(emWoSearch.toLowerCase())) return false;
      return true;
    }).sort((a,b)=>(a.event_date||a.date||'').localeCompare(b.event_date||b.date||''));

    const filteredWOs = filterWOs();

    // ── Selection ──────────────────────────────────────────────
    const toggleSel = useCallback((id) => setSelIds(s=>{ const n=new Set(s); n.has(id)?n.delete(id):n.add(id); return n; }), []);
    const selectAll = (checked) => setSelIds(checked ? new Set(filteredWOs.map(w=>w.id)) : new Set());

    // ── Actions ────────────────────────────────────────────────
    const holdEntry = async (wo) => {
      const name = wo.title||wo.day_name||`WO-${wo.id}`;
      const reason = window.prompt(`Put "${name}" on Hold?\n\nOptional reason for hold:`, '');
      if (reason===null) return; // cancelled
      try {
        // Send both status fields — server may use either
        await api.put('/work-orders/'+wo.id, {
          status:        'On Hold',
          cached_status: 'On Hold',
          hold_reason:   reason.trim(),
          prev_status:   wo.cached_status||wo.status||'Draft',
        });
        refreshWOs();
      } catch(e) {
        const msg = e.response?.data?.message || e.message;
        alert('Hold failed: '+msg);
      }
    };

    const resumeEntry = async (wo) => {
      const prevSt = wo.prev_status || 'Draft';
      try {
        await api.put('/work-orders/'+wo.id, {
          status:        prevSt,
          cached_status: prevSt,
          hold_reason:   '',
        });
        refreshWOs();
      } catch(e) {
        alert('Resume failed: '+(e.response?.data?.message||e.message));
      }
    };

    const deleteEntry = async (wo) => {
      if (!window.confirm(`Delete ${wo.wo_number||`WO-${wo.id}`} — "${wo.title||wo.day_name}"?\n\nThis cannot be undone.`)) return;
      try { await api.delete('/work-orders/'+wo.id); refreshWOs(); }
      catch(e) { alert('Delete failed: '+(e.response?.data?.message||e.message)); }
    };

    // ── Bulk Hold ──────────────────────────────────────────────
    const bulkHold = async () => {
      if (!selIds.size) { alert('Select entries first'); return; }
      const reason = window.prompt(`Put ${selIds.size} entries on Hold?\n\nOptional reason:`, '');
      if (reason===null) return;
      let failed=0;
      for (const id of [...selIds]) {
        const wo = localWOs.find(w=>w.id===id);
        try { await api.put('/work-orders/'+id, { status:'On Hold', cached_status:'On Hold', hold_reason:reason.trim(), prev_status:wo?.cached_status||wo?.status||'Draft' }); }
        catch(e){ failed++; }
      }
      if (failed) alert(`${selIds.size-failed} put on hold, ${failed} failed.`);
      setSelIds(new Set()); refreshWOs();
    };

    // ── Bulk Workflow Type ────────────────────────────────────
    const applyBulkWFType = async () => {
      if (!bulkWFType) { alert('Select a workflow type first'); return; }
      let failed = 0;
      for (const id of [...selIds]) {
        try { await api.put('/work-orders/'+id, { campaign_type_id:parseInt(bulkWFType) }); }
        catch(e){ failed++; console.error('Bulk WF type failed for WO '+id, e.response?.data); }
      }
      if (failed) alert(`${selIds.size-failed} updated, ${failed} failed. Check console.`);
      setBulkModal(null); setBulkWFType(''); setSelIds(new Set()); refreshWOs();
    };

    // ── Bulk Output Formats ───────────────────────────────────
    const applyBulkFormats = async () => {
      if (!bulkFormats.length) { alert('Select at least one format'); return; }
      let failed = 0;
      for (const id of [...selIds]) {
        try { await api.put('/work-orders/'+id, { output_formats:bulkFormats }); }
        catch(e){ failed++; console.error('Bulk formats failed for WO '+id, e.response?.data?.message); }
      }
      if (failed) alert(`${selIds.size-failed} updated, ${failed} failed.`);
      setBulkModal(null); setBulkFormats([]); setSelIds(new Set()); refreshWOs();
    };

    // ── Bulk Platforms ────────────────────────────────────────
    const applyBulkPlatforms = async () => {
      if (!bulkPlatforms.length) { alert('Select at least one platform'); return; }
      let failed = 0;
      for (const id of [...selIds]) {
        try {
          let finalPlatforms = bulkPlatforms;
          if (bulkPlatformMode === 'add') {
            const wo = localWOs.find(w => w.id === id);
            const existing = parseArr(wo?.platforms);
            finalPlatforms = [...new Set([...existing, ...bulkPlatforms])];
          }
          await api.put('/work-orders/'+id, { platforms: finalPlatforms });
        }
        catch(e){ failed++; console.error('Bulk platforms failed for WO '+id, e.response?.data?.message); }
      }
      if (failed) alert(`${selIds.size-failed} updated, ${failed} failed.`);
      setBulkModal(null); setBulkPlatforms([]); setBulkPlatformMode('replace'); setSelIds(new Set()); refreshWOs();
    };

    const exportCSV = () => {
      const rows=[['WO #','Event Date','Client','Event/Day','Category','Status'],...filteredWOs.map(w=>[w.wo_number||`WO-${w.id}`,w.event_date||w.date||'',clientName(w.client),w.title||w.day_name||'',w.category||'',getStatus(w)])];
      const csv=rows.map(r=>r.map(c=>`"${String(c||'').replace(/"/g,'""')}"`).join(',')).join('\n');
      const a=document.createElement('a');a.href='data:text/csv,'+encodeURIComponent(csv);a.download='content_planner.csv';a.click();
    };

    const calLabel  = lcAll ? `${MONTHS_SHORT[lcM]} ${lcY} → ${MONTHS_SHORT[(lcM+5)%12]} ${lcM+5>11?lcY+1:lcY}` : `${MONTHS_SHORT[lcM]} ${lcY}`;
    const entryLabel= emAll ? `${MONTHS_SHORT[emM]} ${emY} → ${MONTHS_SHORT[(emM+5)%12]} ${emM+5>11?emY+1:emY}` : `📅 ${MONTHS_LONG[emM]} ${emY}`;

    // ── Parse arrays from WO ──────────────────────────────────
    const parseArr = (v) => { if (!v) return []; if (Array.isArray(v)) return v; try { return JSON.parse(v); } catch(e) { return []; } };

    const PLAT_META = {
      'Instagram':{'color':'#e8323c','icon':'📸'},'Facebook':{'color':'#1877f2','icon':'👥'},
      'YouTube':{'color':'#ff0000','icon':'▶️'},'LinkedIn':{'color':'#0077b5','icon':'💼'},
      'X / Twitter':{'color':'#1da1f2','icon':'🐦'},'WhatsApp':{'color':'#25d366','icon':'💬'},
      'Google Ads':{'color':'#4285f4','icon':'🎯'},'Meta Ads':{'color':'#0668e1','icon':'📘'},
    };
    const pm = (name) => PLAT_META[name] || { color:'#6b7280', icon:'📱' };

    const filtOF = (outputFormats||[]).filter(f => !bulkFormatSearch || f.name?.toLowerCase().includes(bulkFormatSearch.toLowerCase()) || f.platform?.toLowerCase().includes(bulkFormatSearch.toLowerCase()));

    return (
      <div>
        {/* ── Entry Modal (Add/Edit) ── */}
        {modal && (
          <EntryModal
            mode={modal.mode} entry={modal.entry} prefillDate={modal.prefillDate}
            platforms={platforms} campaignTypes={campaignTypes} outputFormats={outputFormats}
            roles={roles} teamMembers={teamMembers}
            weeklyOff={weeklyOff} holidays={holidays} leadDays={leadDays}
            onSave={refreshWOs} onClose={()=>setModal(null)}
          />
        )}

        {/* ── Bulk Workflow Type Modal ── */}
        {bulkModal==='workflow' && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:9998,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={e=>e.target===e.currentTarget&&setBulkModal(null)}>
            <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',boxShadow:'0 20px 60px rgba(0,0,0,.3)',width:'420px',maxWidth:'95vw',padding:'24px'}}>
              <div className="fw-700 mb-14">🎯 Assign Workflow Type <span className="badge badge-blue" style={{marginLeft:'8px'}}>{selIds.size} selected</span></div>
              <div className="form-group">
                <label className="form-label">Select Workflow Type</label>
                <select className="form-select" value={bulkWFType} onChange={e=>setBulkWFType(e.target.value)}>
                  <option value="">— Select —</option>
                  {(campaignTypes||[]).map(ct=><option key={ct.id} value={ct.id}>{ct.name}</option>)}
                </select>
              </div>
              <div className="text-xs text-muted mb-14">Will be applied to all {selIds.size} selected entries.</div>
              <div className="flex" style={{gap:'8px',justifyContent:'flex-end'}}>
                <button className="btn btn-secondary" onClick={()=>setBulkModal(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={applyBulkWFType}>✓ Apply to {selIds.size} entries</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Bulk Output Formats Modal ── */}
        {bulkModal==='formats' && (
          <BulkFormatsModal
            outputFormats={outputFormats}
            selected={bulkFormats}
            onToggle={(fname)=>setBulkFormats(prev=>
              prev.includes(fname)?prev.filter(s=>s!==fname):[...prev,fname]
            )}
            onClear={()=>setBulkFormats([])}
            onClose={()=>setBulkModal(null)}
            onApply={applyBulkFormats}
            selCount={selIds.size}
          />
        )}
        {/* ── Bulk Platforms Modal ── */}
        {bulkModal==='platforms' && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:9998,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={e=>e.target===e.currentTarget&&setBulkModal(null)}>
            <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',boxShadow:'0 20px 60px rgba(0,0,0,.3)',width:'560px',maxWidth:'96vw',maxHeight:'90vh',display:'flex',flexDirection:'column'}}>

              {/* Header */}
              <div style={{padding:'14px 20px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
                <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                  <span style={{fontSize:'1.1rem'}}>📱</span>
                  <span className="fw-700" style={{fontSize:'0.95rem'}}>Assign Platforms</span>
                  <span className="badge badge-blue" style={{fontSize:'0.72rem'}}>{selIds.size} selected</span>
                </div>
                <button onClick={()=>setBulkModal(null)} style={{background:'none',border:'none',fontSize:'1.1rem',cursor:'pointer',color:'var(--text3)'}}>✕</button>
              </div>

              {/* Info */}
              <div style={{padding:'8px 20px',borderBottom:'1px solid var(--border)',fontSize:'0.75rem',color:'var(--text3)',background:'var(--bg3)',flexShrink:0}}>
                Tick one or more platforms — only the entry's <strong>Platforms</strong> field is updated. Output Formats stay untouched (use the 📐 Output Formats button for those).
              </div>

              {/* Replace / Add radio */}
              <div style={{padding:'8px 20px',borderBottom:'1px solid var(--border)',display:'flex',gap:'20px',alignItems:'center',flexShrink:0}}>
                {[['replace','Replace existing platforms'],['add','Add to existing platforms']].map(([val,label])=>(
                  <label key={val} style={{display:'flex',alignItems:'center',gap:'6px',cursor:'pointer',fontSize:'0.8rem',fontWeight:bulkPlatformMode===val?700:400,color:bulkPlatformMode===val?'var(--accent)':'var(--text2)'}}>
                    <input type="radio" name="platMode" value={val} checked={bulkPlatformMode===val} onChange={()=>setBulkPlatformMode(val)} style={{accentColor:'var(--accent)',width:'14px',height:'14px'}} />
                    {label}
                  </label>
                ))}
              </div>

              {/* 2-col platform card grid */}
              <div style={{overflowY:'auto',flex:1,padding:'14px 20px'}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                  {(platforms||[]).filter(p=>p.enabled!==false&&p.is_active!==false).map(p=>{
                    const pcolor = p.color||PLATFORM_COLORS[p.name]||'#6b7280';
                    const svg    = getPlatformSvg(p.name);
                    const checked = bulkPlatforms.includes(p.name);
                    const fmtCount = (outputFormats||[]).filter(f=>f.enabled!==false&&f.platform===p.name).length;
                    return (
                      <div key={p.id}
                        onClick={()=>setBulkPlatforms(prev=>prev.includes(p.name)?prev.filter(x=>x!==p.name):[...prev,p.name])}
                        style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 12px',borderRadius:'8px',cursor:'pointer',border:`1.5px solid ${checked?pcolor:'var(--border)'}`,background:checked?`${pcolor}0f`:'var(--bg2)',borderLeft:`4px solid ${checked?pcolor:pcolor+'44'}`,transition:'border-color 0.15s,background 0.15s'}}>
                        <input type="checkbox" checked={checked} onChange={()=>{}} style={{accentColor:pcolor,width:'15px',height:'15px',flexShrink:0,pointerEvents:'none'}} />
                        <span style={{width:'26px',height:'26px',flexShrink:0,borderRadius:'6px',overflow:'hidden',display:'inline-flex',alignItems:'center',justifyContent:'center',background:checked?`${pcolor}22`:'var(--bg3)'}}>
                          {svg ? <span style={{width:'22px',height:'22px',display:'inline-flex'}} dangerouslySetInnerHTML={{__html:svg}} /> : <span style={{fontSize:'1rem'}}>{p.icon||'📱'}</span>}
                        </span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:'0.82rem',fontWeight:checked?700:500,color:checked?pcolor:'var(--text)',lineHeight:1.2}}>{p.name}</div>
                          {fmtCount>0&&<div style={{fontSize:'0.65rem',color:'var(--text3)',marginTop:'2px'}}>{fmtCount} format{fmtCount!==1?'s':''}</div>}
                        </div>
                        {checked&&<span style={{width:'18px',height:'18px',borderRadius:'50%',background:pcolor,display:'inline-flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:'0.62rem',color:'#fff',fontWeight:700}}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Footer */}
              <div style={{padding:'12px 20px',borderTop:'1px solid var(--border)',display:'flex',gap:'8px',justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
                <span style={{fontSize:'0.75rem',color:'var(--text3)'}}>
                  {bulkPlatforms.length>0
                    ? <><strong style={{color:'var(--accent)'}}>{bulkPlatforms.length}</strong> platform{bulkPlatforms.length!==1?'s':''} selected — will <strong>{bulkPlatformMode==='add'?'add to':'replace'}</strong> existing</>
                    : 'Select at least one platform'}
                </span>
                <div style={{display:'flex',gap:'8px'}}>
                  <button className="btn btn-secondary" onClick={()=>setBulkModal(null)}>Cancel</button>
                  <button className="btn btn-primary" onClick={applyBulkPlatforms}
                    disabled={!bulkPlatforms.length} style={{opacity:bulkPlatforms.length?1:0.5}}>
                    ✓ Apply to {selIds.size} {selIds.size===1?'entry':'entries'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Calendar ── */}
        <div className="card mb-16">
          <div className="card-header">
            <div className="card-title" style={{fontSize:'0.85rem'}}>
              📅 Monthly Planner
              <span className="badge badge-blue" style={{fontSize:'0.68rem',padding:'3px 8px',marginLeft:'6px'}}>{calLabel}</span>
            </div>
            <div style={{display:'flex',gap:'6px',flexWrap:'wrap',alignItems:'center'}}>
              <select className="form-select" value={lcM} onChange={e=>setCalMonth(parseInt(e.target.value),lcY)} style={{padding:'4px 8px',fontSize:'0.72rem',width:'auto'}}>
                {MONTHS_LONG.map((m,i)=><option key={i} value={i}>{m}</option>)}
              </select>
              <select className="form-select" value={lcY} onChange={e=>setCalMonth(lcM,parseInt(e.target.value))} style={{padding:'4px 8px',fontSize:'0.72rem',width:'auto'}}>
                {[2025,2026,2027,2028].map(y=><option key={y}>{y}</option>)}
              </select>
              <button onClick={toggleAll} style={{padding:'4px 10px',fontSize:'0.72rem',fontWeight:700,borderRadius:'6px',border:`1px solid ${lcAll?'var(--accent)':'var(--border)'}`,background:lcAll?'var(--accent)':'var(--bg3)',color:lcAll?'#fff':'var(--text2)',cursor:'pointer'}}>
                {lcAll?'📅 Single Month':'∞ All Months'}
              </button>
              <select className="form-select" value={lcCl} onChange={e=>setLcCl(e.target.value)} style={{padding:'4px 8px',fontSize:'0.72rem',width:'auto',minWidth:'120px'}}>
                <option value="">All Clients</option>
                {uniqueClients.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
              <button className="btn btn-secondary" style={{fontSize:'0.72rem',padding:'4px 10px'}} onClick={()=>goStep(3)}>← Back</button>
              <button className="btn btn-secondary" style={{fontSize:'0.72rem',padding:'4px 10px'}} onClick={exportCSV}>📥 Export CSV</button>
              <button className="btn btn-secondary" style={{fontSize:'0.72rem',padding:'4px 10px'}} onClick={()=>window.print()}>🖨️ Print</button>
              <button onClick={()=>navigate('/content')} style={{padding:'4px 12px',fontSize:'0.72rem',fontWeight:700,background:'#6c58f0',color:'#fff',border:'none',borderRadius:'6px',cursor:'pointer'}}>📝 Content Briefs →</button>
            </div>
          </div>

          {lcAll ? (
            <div style={{padding:'12px'}}>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'12px'}}>
                {Array.from({length:6},(_,i)=>{ const d=new Date(lcY,lcM+i,1); return (
                  <MiniMonth key={`${d.getMonth()}-${d.getFullYear()}`} m={d.getMonth()} y={d.getFullYear()}
                    localWOs={localWOs} masterDays={masterDays} lcCl={lcCl}
                    clientColors={clientColors} todayStr={todayStr}
                    setModal={setModal} setCalMonth={setCalMonth} setLcAll={setLcAll} setEmAll={setEmAll} />
                ); })}
              </div>
              <div style={{marginTop:'10px',padding:'10px 14px',background:'var(--bg3)',borderTop:'1px solid var(--border)',fontSize:'0.7rem',color:'var(--text3)',display:'flex',flexWrap:'wrap',gap:'14px',alignItems:'center'}}>
                <span>— Special day (category color)</span>
                <span>— Client campaign (color-coded by client)</span>
                <span style={{marginLeft:'auto'}}>Click month title to zoom in · Click day to add entry · Click bar to edit</span>
              </div>
            </div>
          ) : (
            <FullMonth m={lcM} y={lcY} localWOs={localWOs} masterDays={masterDays} lcCl={lcCl}
              clientColors={clientColors} todayStr={todayStr} setModal={setModal} />
          )}
        </div>

        {/* ── Entries Table ── */}
        <div className="card" style={{overflow:'hidden',display:'flex',flexDirection:'column',maxHeight:'75vh'}}>
          <div style={{background:'var(--bg2)',borderBottom:'1px solid var(--border)',flexShrink:0}}>
            {/* Row 1: Title + month nav */}
            <div style={{padding:'8px 14px',display:'flex',alignItems:'center',flexWrap:'wrap',gap:'6px'}}>
              <div className="card-title" style={{fontSize:'0.85rem'}}>
                ✏️ Calendar Entries
                <span className="badge badge-gray" style={{fontSize:'0.65rem',marginLeft:'6px'}}>
                  {filteredWOs.length} entr{filteredWOs.length!==1?'ies':'y'}{emAll?' across 6 months':''}
                </span>
              </div>
              <div style={{display:'flex',gap:'5px',flex:1,justifyContent:'center',alignItems:'center',flexWrap:'wrap'}}>
                {['prev','current','next'].map(d=>(
                  <button key={d} onClick={()=>goEntryMonth(d)} style={{padding:'4px 10px',fontSize:'0.7rem',fontWeight:600,borderRadius:'20px',cursor:'pointer',border:d==='current'?'1px solid var(--accent)':'1px solid var(--border)',background:d==='current'?'var(--accent)':'var(--bg3)',color:d==='current'?'#fff':'var(--text2)'}}>
                    {d==='prev'?'‹ Prev':d==='current'?'● Current':'Next ›'}
                  </button>
                ))}
                {/* Month checkbox dropdown */}
                <div style={{position:'relative'}}>
                  <button onClick={()=>setEmMonthOpen(v=>!v)}
                    style={{padding:'4px 8px',fontSize:'0.72rem',fontWeight:600,borderRadius:6,border:'1px solid var(--border)',background:'var(--bg3)',color:'var(--text)',cursor:'pointer',display:'flex',alignItems:'center',gap:5,minWidth:110,justifyContent:'space-between',whiteSpace:'nowrap'}}>
                    <span style={{overflow:'hidden',textOverflow:'ellipsis',maxWidth:110}}>
                      {emMonths.length===0?'All Months':emMonths.length===1?MONTHS_LONG[emMonths[0]]:emMonths.map(i=>MONTHS_SHORT[i]).join(', ')}
                    </span>
                    <svg viewBox="0 0 10 6" width="10" height="6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M1 1l4 4 4-4"/></svg>
                  </button>
                  {emMonthOpen && (
                    <>
                      <div onClick={()=>setEmMonthOpen(false)} style={{position:'fixed',inset:0,zIndex:199}}/>
                      <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:8,zIndex:200,minWidth:160,boxShadow:'0 8px 24px rgba(0,0,0,0.15)',padding:'6px 0'}}>
                        <label style={{display:'flex',alignItems:'center',gap:8,padding:'6px 14px',cursor:'pointer',fontSize:'0.78rem',borderBottom:'1px solid var(--border)',fontWeight:700,color:'var(--accent)'}}>
                          <input type="checkbox" checked={emMonths.length===0}
                            onChange={()=>setEmMonths([])}
                            style={{accentColor:'var(--accent)',width:14,height:14}}/>
                          All Months
                        </label>
                        {MONTHS_LONG.map((m,i)=>(
                          <label key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 14px',cursor:'pointer',fontSize:'0.78rem',background:emMonths.includes(i)?'rgba(59,126,240,0.06)':'transparent'}}>
                            <input type="checkbox" checked={emMonths.includes(i)}
                              onChange={()=>{
                                const next = emMonths.includes(i) ? emMonths.filter(x=>x!==i) : [...emMonths,i].sort((a,b)=>a-b);
                                setEmMonths(next);
                                if(next.length>0){setEmM(next[0]);setLcM(next[0]);}
                              }}
                              style={{accentColor:'var(--accent)',width:14,height:14}}/>
                            {m}
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <select className="form-select" value={emY} onChange={e=>{const y=parseInt(e.target.value);setEmY(y);setLcY(y);}} style={{padding:'4px 8px',fontSize:'0.72rem',width:'auto'}}>
                  {[2025,2026,2027,2028].map(y=><option key={y}>{y}</option>)}
                </select>
                <select className="form-select" value={lcCl} onChange={e=>setLcCl(e.target.value)} style={{padding:'4px 8px',fontSize:'0.72rem',width:'auto',minWidth:'110px'}}>
                  <option value="">All Clients</option>
                  {uniqueClients.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
                <div style={{position:'relative',display:'flex',alignItems:'center'}}>
                  <input className="form-input" value={emWoSearch} onChange={e=>setEmWoSearch(e.target.value)}
                    placeholder="Search WO #..." style={{padding:'4px 22px 4px 8px',fontSize:'0.72rem',width:'110px'}} />
                  {emWoSearch && <span onClick={()=>setEmWoSearch('')} style={{position:'absolute',right:8,cursor:'pointer',color:'var(--text3)',fontSize:'0.8rem'}}>✕</span>}
                </div>
              </div>
              <button className="btn btn-primary" style={{fontSize:'0.72rem',padding:'4px 12px'}} onClick={()=>setModal({mode:'add'})}>+ Add Entry</button>
              <button onClick={()=>navigate('/content')} style={{padding:'4px 12px',fontSize:'0.72rem',fontWeight:700,background:'#6c58f0',color:'#fff',border:'none',borderRadius:'6px',cursor:'pointer'}}>📝 Content Briefs →</button>
            </div>
            {/* Row 2: Bulk actions */}
            <div style={{padding:'8px 14px',background:'var(--bg3)',borderTop:'1px solid var(--border)',display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
              <label style={{display:'flex',alignItems:'center',gap:'5px',fontSize:'0.78rem',cursor:'pointer',fontWeight:600}}>
                <input type="checkbox" onChange={e=>selectAll(e.target.checked)} checked={selIds.size>0&&selIds.size===filteredWOs.length}
                  style={{accentColor:'#6c58f0',width:'14px',height:'14px',cursor:'pointer'}} /> Select All
              </label>
              <span className="text-xs fw-700 text-muted">BULK:</span>
              <button onClick={()=>{if(!selIds.size){alert('Select entries first');return;}setBulkModal('workflow');}} style={{padding:'4px 12px',fontSize:'0.75rem',fontWeight:700,background:'var(--bg2)',color:'var(--accent)',border:'1px solid var(--accent)',borderRadius:'6px',cursor:'pointer'}}>🎯 Workflow Type</button>
              <button onClick={()=>{if(!selIds.size){alert('Select entries first');return;}setBulkModal('formats');setBulkFormatSearch('');}} style={{padding:'4px 12px',fontSize:'0.75rem',fontWeight:700,background:'var(--bg2)',color:'#1db87a',border:'1px solid #1db87a',borderRadius:'6px',cursor:'pointer'}}>📐 Output Formats</button>
              <button onClick={()=>{if(!selIds.size){alert('Select entries first');return;}setBulkModal('platforms');setBulkPlatforms([]);setBulkPlatformMode('replace');}} style={{padding:'4px 12px',fontSize:'0.75rem',fontWeight:700,background:'var(--bg2)',color:'#9050e8',border:'1px solid #9050e8',borderRadius:'6px',cursor:'pointer'}}>📱 Platforms</button>
              {selIds.size>0&&<><span className="text-xs text-muted">{selIds.size} selected</span><button onClick={()=>setSelIds(new Set())} style={{padding:'3px 8px',fontSize:'0.7rem',fontWeight:600,background:'var(--bg2)',color:'var(--text2)',border:'1px solid var(--border)',borderRadius:'6px',cursor:'pointer'}}>✕ Clear</button></>}
            </div>
          </div>

          {/* Table */}
          <div style={{overflowY:'auto',flex:1}}>
            {filteredWOs.length===0 ? (
              <div style={{padding:'40px',textAlign:'center',color:'var(--text3)'}}>
                <div style={{fontSize:'2rem',marginBottom:'8px'}}>📅</div>
                <div className="fw-700">No entries for {entryLabel}</div>
                <div className="text-xs text-muted" style={{marginTop:'6px'}}>Click any day on the calendar above to add an entry, or use + Add Entry.</div>
              </div>
            ) : (
              <table className="data-table">
                <thead style={{position:'sticky',top:0,zIndex:10,background:'var(--bg2)'}}>
                  <tr>
                    <th style={{width:'30px'}} />
                    <th style={{whiteSpace:'nowrap'}}>WO #</th>
                    <th style={{whiteSpace:'nowrap'}}>Event Date</th>
                    <th>Client</th>
                    <th>Event / Day</th>
                    <th>Category</th>
                    <th style={{whiteSpace:'nowrap'}}>🎯 Workflow</th>
                    <th style={{whiteSpace:'nowrap'}}>📐 Fmts</th>
                    <th style={{whiteSpace:'nowrap'}}>📱 Plat.</th>
                    <th style={{whiteSpace:'nowrap'}}>👤 Coordinator</th>
                    <th style={{whiteSpace:'nowrap'}}>🎨 Creatives Making</th>
                    <th style={{whiteSpace:'nowrap'}}>📅 Sched. Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWOs.map(wo=>{
                    const cn=clientName(wo.client);
                    const d=wo.event_date||wo.date||'';
                    const [ey,em,ed]=d.split('-');
                    const displayDate=ed?`${ed}-${em}-${ey}`:'—';
                    const st=getStatus(wo);
                    const isHold=st==='On Hold';
                    const wp=workflowPos(wo);
                    const plats=parseArr(wo.platforms);
                    const fmts=parseArr(wo.output_formats||wo.outputFormats);
                    const ct=wo.campaign_type||wo.campaignType;
                    const catStr=typeof wo.category==='object'?wo.category?.name:wo.category;

                    // Use server-resolved role map {roleName: userName}
                    const rr = wo.resolved_team_roles || {};
                    const coordinator = rr['Coordinator'] || '—';
                    // Find 'Creatives Making' phase owner role from this WO's workflow type, then resolve
                    // Creatives Making: pick role based on output format types
                    // Video formats → Video Editor/Videographer; Image formats → Graphic Designer
                    const creativeDir = (() => {
                      const VIDEO_KEYWORDS = ['reel', 'video', 'film', 'motion', 'animate', 'videograph', 'short'];
                      const fmtNames = fmts.map(f => (typeof f==='object' ? (f.name||'') : f).toLowerCase());
                      const hasVideo = fmtNames.some(n => VIDEO_KEYWORDS.some(k => n.includes(k)));
                      const hasImage = fmtNames.some(n => !VIDEO_KEYWORDS.some(k => n.includes(k)));

                      // Role name candidates
                      const videoRoles = ['Video Editor', 'Videographer', 'Video Producer', 'Motion Designer'];
                      const imageRoles = ['Graphic Designer', 'Designer', 'Art Director', 'Creative Designer'];

                      const resolveFirst = (roleList) => {
                        for (const r of roleList) { if (rr[r]) return rr[r]; }
                        return null;
                      };

                      if (hasVideo && hasImage) {
                        // Mixed — show both if different people
                        const vPerson = resolveFirst(videoRoles);
                        const iPerson = resolveFirst(imageRoles);
                        if (vPerson && iPerson && vPerson !== iPerson) return `${iPerson} / ${vPerson}`;
                        return vPerson || iPerson || '—';
                      }
                      if (hasVideo) return resolveFirst(videoRoles) || resolveFirst(imageRoles) || '—';
                      if (hasImage) return resolveFirst(imageRoles) || resolveFirst(videoRoles) || '—';
                      // No formats yet — fall back to phase owner from workflow
                      const ctId = wo.campaign_type_id || wo.campaign_type?.id;
                      const wct = (campaignTypes||[]).find(c=>String(c.id)===String(ctId));
                      if (!wct) return '—';
                      const phases = Array.isArray(wct.phases) ? wct.phases : (() => {try{return JSON.parse(wct.phases||'[]');}catch(e){return [];}})();
                      const ph = phases.find(p=>(p.name||'').toLowerCase().includes('creatives making') || (p.name||'').toLowerCase().includes('creative making'));
                      if (!ph) return '—';
                      const ownerRole = ph.owner || (ph.owners||[])[0];
                      return ownerRole ? (rr[ownerRole] || '—') : '—';
                    })();
                    const schedDate   = (() => { const s=wo.scheduling_date||''; if(!s) return '—'; const [sy,sm,sd]=s.split('-'); return sd?`${sd}-${sm}-${sy}`:'—'; })();
                    return (
                      <tr key={wo.id} style={{background:isHold?'rgba(232,120,48,0.04)':undefined}}>
                        <td onClick={e=>e.stopPropagation()}>
                          <input type="checkbox" checked={selIds.has(wo.id)}
                            onChange={e=>{e.stopPropagation();toggleSel(wo.id);}}
                            style={{accentColor:'#6c58f0',width:'14px',height:'14px',cursor:'pointer'}} />
                        </td>
                        <td><span className="mono fw-700" style={{fontSize:'0.72rem',color:'var(--accent)'}}>{wo.wo_number||`WO-${wo.id}`}</span></td>
                        <td className="mono text-sm fw-700" style={{whiteSpace:'nowrap'}}>{displayDate}</td>
                        <td><span className="badge badge-blue">{cn}</span></td>
                        <td className="fw-700" style={{maxWidth:130}}>{wo.title||wo.day_name||wo.dayName||'—'}</td>
                        <td>{catStr?<CatPill cat={catStr} />:<span className="text-xs text-muted">—</span>}</td>
                        <td>
                          {ct
                            ? <span style={{display:'inline-flex',alignItems:'center',gap:'4px',padding:'2px 6px',background:'rgba(59,126,240,0.07)',border:'1px solid rgba(59,126,240,0.2)',borderRadius:'10px',fontSize:'0.65rem',fontWeight:600,color:'var(--accent)',whiteSpace:'nowrap'}}>🎯 {typeof ct==='object'?ct.name:ct}</span>
                            : <span style={{fontSize:'0.65rem',color:'#e87830',fontWeight:600}} title="Edit entry to assign Workflow Type">⚠️ Not set</span>}
                        </td>
                        <td style={{whiteSpace:'nowrap'}}>
                          {fmts.length
                            ? <span style={{display:'inline-flex',alignItems:'center',gap:'4px',padding:'2px 6px',background:'rgba(29,184,122,0.08)',border:'1px solid rgba(29,184,122,0.2)',borderRadius:'10px',fontSize:'0.65rem',fontWeight:600,color:'#1db87a'}}>📐 {fmts.length}</span>
                            : <span style={{fontSize:'0.65rem',color:'#e87830',fontWeight:600}} title="Edit entry to assign Output Formats">⚠️</span>}
                        </td>
                        <td>
                          {plats.length>0
                            ? <div style={{display:'flex',gap:'3px',flexWrap:'nowrap'}}>
                                {plats.slice(0,3).map((p,pi)=>{ const pname=typeof p==='object'?p.name:p;
                                  const pcolor=PLATFORM_COLORS[pname]||'#6b7280';
                                  const svg=getPlatformSvg(pname);
                                  return <span key={pi} title={pname} style={{display:'inline-flex',alignItems:'center',justifyContent:'center',width:'22px',height:'22px',borderRadius:'50%',background:`${pcolor}14`,border:`1px solid ${pcolor}55`,overflow:'hidden',flexShrink:0}}>
                                    {svg ? <span style={{width:'14px',height:'14px',display:'inline-flex'}} dangerouslySetInnerHTML={{__html:svg}} /> : <span style={{fontSize:'0.7rem'}}>📱</span>}
                                  </span>;
                                })}
                                {plats.length>3&&<span className="text-xs text-muted">+{plats.length-3}</span>}
                              </div>
                            : <span style={{fontSize:'0.65rem',color:'#e87830',fontWeight:600}}>⚠️ —</span>}
                        </td>
                        <td style={{fontSize:'0.72rem',whiteSpace:'nowrap'}}>{coordinator}</td>
                        <td style={{fontSize:'0.72rem',whiteSpace:'nowrap'}}>{creativeDir}</td>
                        <td className="mono text-sm fw-700" style={{whiteSpace:'nowrap',color:schedDate==='—'?'var(--text3)':'var(--text1)'}} title={isHold&&wo.hold_reason?`On Hold: ${wo.hold_reason}`:wo.notes||''}>{schedDate}{(isHold&&wo.hold_reason)||wo.notes?<span title={(isHold&&wo.hold_reason)||wo.notes||''} style={{marginLeft:4,cursor:'help',color:'#e87830'}}>📝</span>:null}</td>
                        <td>
                          <span title="Workflow position — derived from current phase state" style={{display:'inline-block',padding:'3px 8px',fontSize:'0.65rem',fontWeight:700,color:wp.color,background:wp.bg,border:`1px solid ${wp.border}`,borderRadius:'10px',whiteSpace:'nowrap'}}>{wp.label}</span>
                        </td>
                        <td>
                          <div className="flex" style={{gap:'4px'}}>
                            <button className="btn btn-secondary" style={{padding:'2px 7px',fontSize:'0.68rem'}} title="Edit entry" onClick={()=>setModal({mode:'edit',entry:wo})}>✏️</button>
                            {isHold
                              ? <button title="Resume" style={{padding:'2px 7px',fontSize:'0.68rem',color:'#1db87a',border:'1px solid #1db87a',borderRadius:'var(--radius-sm)',background:'var(--bg2)',cursor:'pointer'}} onClick={()=>resumeEntry(wo)}>▶ Resume</button>
                              : st!=='Done'&&st!=='Cancelled'
                                ? <button title="Put on Hold" style={{padding:'2px 7px',fontSize:'0.68rem',color:'#e87830',border:'1px solid #e87830',borderRadius:'var(--radius-sm)',background:'var(--bg2)',cursor:'pointer'}} onClick={()=>holdEntry(wo)}>⏸ Hold</button>
                                : null}
                            <button className="btn btn-red" style={{padding:'2px 7px',fontSize:'0.68rem'}} title="Delete entry" onClick={()=>deleteEntry(wo)}>✕</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  };




  // ── Render ────────────────────────────────────────────────────
  return (
    <div style={{padding:'8px 24px 24px',overflowX:'hidden',maxWidth:'100%',boxSizing:'border-box'}}>
      <StepBar />
      {step===1 && <Step1 />}
      {step===2 && <Step2 />}
      {step===3 && <Step3 />}
      {step===4 && <Step4 platforms={platforms} campaignTypes={campaignTypes} outputFormats={outputFormats} roles={roles} teamMembers={teamMembers} weeklyOff={weeklyOff} holidays={holidays} leadDays={leadDays} />}
    </div>
  );
}